import { useState, useEffect, useContext, useRef, useMemo, useCallback, memo } from 'react';
import { isPhotosLaterEnabled, isGiveawayPromoEnabled } from '../featureFlags';
import { normalizeOfferMode, OFFER_MODE_LABELS, OFFER_MODE_DESCRIPTIONS } from '../utils/offerMode';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  rectSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp, updateDoc, doc, query, where, getDocs, deleteField } from 'firebase/firestore';
import { auth } from '../firebase';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { UserContext } from '../contexts/UserContext';
import { useContact } from '../contexts/ContactContext';
import { PEDAL_COLORS, trackMissingPedal } from '../utils/pedalDatabase';
import { trackPedalAdded } from '../utils/effectAnalytics';
import { formatCurrencyInput } from '../utils/currencyUtils';
import { resolvePriceDropChange } from '../utils/priceDropPolicy';
import { validateMessage } from '../utils/profanityFilter';
import imageCompression from 'browser-image-compression';
import { logError } from '../utils/errorLogger';
import { Link } from 'react-router-dom';
import { useSellingDisabled } from '../hooks/useSellingDisabled';
import SellingDisabledBanner from './SellingDisabledBanner';
import PriceIndicator from './PriceIndicator';
import ConditionDescriptionPopover from './ConditionDescriptionPopover';
import { CONDITION_DESCRIPTIONS } from '../utils/conditionDescriptions';
import { useReverbProductSuggestions, useReverbBrandSuggestions, useReverbCombinedSuggestions, prewarmReverbSuggestions } from '../hooks/useReverbProductSuggestions';
import { prewarmReverbPricing } from '../hooks/useReverbPricing';
import { scrollPageToTop, scrollElementIntoView } from '../utils/scrollPageToTop';
import { materializePickedFile } from '../utils/materializePickedFile';
import { appendWithCap } from '../utils/photoListCap';
import { maybePromptForPush } from '../services/softPushPrompt';
import { armBannerAroundPrompt } from '../utils/listingBannerTimer';
import { EFFECT_TYPES, effectTypesForReverbCategory } from '../utils/effectTypes';
import { FORM_SELECT_STYLE, FORM_SELECT_INVALID_STYLE } from '../utils/formSelectStyle';
import { buildPlaceholderImage, hasNoPhotos, SKETCH_PLACEHOLDER_URL } from '../utils/listingPlaceholder';
import { titleCaseWords } from '../utils/pedalNameCase';

const PHOTOS_LATER_ENABLED = isPhotosLaterEnabled();
// While the giveaway runs, ticking "Add photos later" shows a one-time warning
// that a photo-less listing earns no entry until real photos are added
// (Jason, 2026-10-20). Trading and buying are unaffected by the warning.
const GIVEAWAY_WARNING_ENABLED = isGiveawayPromoEnabled();

/** Wrapped sort strategy for create-listing thumbnails (wrapped flex rows). */
const createListingPhotoSortStrategy = rectSortingStrategy;

// ✅ Move constant OUTSIDE component so it works in JSX
const DESCRIPTION_LIMIT = 500;

const SLOT_NAMES = ['top', 'back', 'upper', 'lower', 'detail1', 'detail2', 'detail3', 'detail4'];

function orderedFilesToImagesMap(orderedFiles) {
  return SLOT_NAMES.reduce((acc, name, i) => ({ ...acc, [name]: orderedFiles[i] ?? null }), {});
}

const EMPTY_IMAGES_STATE = {
  top: null, back: null, upper: null, lower: null,
  detail1: null, detail2: null, detail3: null, detail4: null
};

const REPLACE_ALL_PHOTOS_CONFIRM = [
  'Replace all photos?',
  '',
  'This will clear every photo currently on this listing from the edit form. Nothing is saved until you tap Save Changes — if you cancel edit or leave without saving, your listing photos stay as they are.',
  '',
  "You'll need to upload at least 2 new photos before you can save.",
  '',
  'Continue?'
].join('\n');

/** True when photos use the bulk orderedFiles pipeline (new add or replace-all in edit). */
function usesBulkPhotoPipeline(editingPedal, replaceAllPhotosMode) {
  // A listing created without photos (placeholder, plan §4) has nothing to
  // replace, so its edit form opens straight on the bulk drop zone — the same
  // uploader a new listing gets — instead of the one-slot-at-a-time editor.
  return !editingPedal || replaceAllPhotosMode || hasNoPhotos(editingPedal);
}

/** True when the user has picked new image files in this session. */
function hasNewSessionImages(useBulk, orderedFiles, images) {
  return useBulk
    ? orderedFiles.length > 0
    : Object.values(images).some((f) => f !== null);
}

function isTouchDevice() {
  return typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches;
}

function SortableCreatePhotoThumb({ id, url, index, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.88 : 1,
    zIndex: isDragging ? 2 : 1
  };
  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        position: 'relative',
        width: 80,
        height: 80,
        flexShrink: 0,
        borderRadius: 6,
        overflow: 'hidden',
        border: '1px solid #ddd',
        background: 'white',
        userSelect: 'none',
        WebkitUserSelect: 'none'
      }}
    >
      <div
        {...attributes}
        {...listeners}
        onDragStart={(e) => e.preventDefault()}
        style={{
          width: '100%',
          height: '100%',
          touchAction: 'none',
          cursor: 'grab',
          userSelect: 'none',
          WebkitUserSelect: 'none'
        }}
      >
        <img
          src={url}
          alt={`Listing photo ${index + 1}`}
          draggable={false}
          onDragStart={(e) => e.preventDefault()}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            pointerEvents: 'none',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            WebkitUserDrag: 'none'
          }}
        />
      </div>
      <span
        style={{
          position: 'absolute',
          left: 4,
          top: 4,
          background: 'rgba(0,0,0,0.7)',
          color: '#fff',
          fontSize: 11,
          fontWeight: 700,
          minWidth: 18,
          height: 18,
          borderRadius: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          lineHeight: 1
        }}
      >
        {index + 1}
      </span>
      <button
        type="button"
        aria-label={`Remove photo ${index + 1}`}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        style={{
          position: 'absolute',
          top: 2,
          right: 2,
          color: 'red',
          fontSize: '0.95rem',
          background: 'white',
          border: 'none',
          borderRadius: '50%',
          width: 22,
          height: 22,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
          lineHeight: 1
        }}
      >
        ×
      </button>
    </div>
  );
}

const EditModeImageTile = memo(function EditModeImageTile({
  slot,
  label,
  required,
  variant,
  diagramSrc,
  placeholderText,
  file,
  existingUrl,
  isRemoved,
  onPick,
  onRemove
}) {
  // Cache the blob URL per `file` so it isn't recreated on every parent render
  // (e.g. while the user types in the description field).
  const previewUrl = useMemo(
    () => (file ? URL.createObjectURL(file) : null),
    [file]
  );
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const showExisting = existingUrl && !isRemoved;
  const labelStyle = variant === 'optional'
    ? { fontWeight: 'bold', marginBottom: '0.25rem', color: '#666' }
    : { fontWeight: 'bold', marginBottom: '0.25rem' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px', backgroundColor: '#f9f9f9' }}>
      <label style={labelStyle}>
        {required && <span style={{ color: 'red' }}>*</span>}{label}
      </label>
      <input
        type="file"
        accept="image/*"
        // Clear at picker-open time (tap AND keyboard activation both dispatch
        // click on the input): iOS names every camera capture image.jpg, and
        // WebKit fires no change event when the new value equals the old one —
        // so retaking a photo into the same tile silently did nothing (iOS,
        // 2026-09-18). Cleared here, never in onChange, so the Android
        // transient-grant copy in handlePickImage always reads a live grant.
        onClick={(e) => { e.currentTarget.value = ''; }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onPick(slot, f);
        }}
        style={{ marginBottom: '0.5rem', textAlign: 'center', width: '100%' }}
      />
      <div className="image-preview-box" style={{ width: '100%', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'white', border: '1px solid #ddd', borderRadius: '4px', minHeight: 0 }}>
        {previewUrl ? (
          <img src={previewUrl} alt={label} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        ) : showExisting ? (
          <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img src={existingUrl} alt={label} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            <button
              type="button"
              onClick={() => onRemove(slot)}
              style={{ position: 'absolute', top: '5px', right: '5px', color: 'red', fontSize: '0.9rem', background: 'white', border: 'none', borderRadius: '50%', width: '20px', height: '20px', cursor: 'pointer' }}
            >
              ×
            </button>
          </div>
        ) : variant === 'optional' ? (
          <div style={{ textAlign: 'center', color: '#999', fontSize: '0.8rem', padding: '1rem' }}>
            <div style={{ marginBottom: '0.5rem' }}>📷</div>
            <div>{placeholderText}</div>
          </div>
        ) : (
          <img src={diagramSrc} alt={label} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
        )}
      </div>
    </div>
  );
});

const ADD_PEDAL_FORM_ID = 'add-pedal-root-form';

const AddPedalForm = ({ editingPedal = null, onFinishEdit, isAdminEdit = false, onOwnsHorizontalGesture, initialFocus = null }) => {
  const { firestoreUser } = useContext(UserContext);
  const { openContact } = useContact();
  const { disabled: sellingDisabled } = useSellingDisabled();
  const successRef = useRef(null);
  const errorRef = useRef(null);
  const revealErrorRef = useRef(null);
  
  const [formData, setFormData] = useState({
  make: '', model: '', condition: '', price: '',
location: '', decade: '', description: '', active: true,
shipping: true, localOnly: false, offerMode: 'open',
effectTypes: [], color: '', youtubeUrl: ''
});




  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const successTimeoutRef = useRef(null);
  const [formResetKey, setFormResetKey] = useState(0);
  // Per-slot File objects for EDIT flow only. On CREATE, this object stays all null — photos live in
  // orderedFiles / previewUrls until submit, then orderedFilesToImagesMap(orderedFiles) feeds the upload pipeline.
  // Do not use `images` for create-path logic; use orderedFiles or orderedFilesToImagesMap instead.
  const [images, setImages] = useState(EMPTY_IMAGES_STATE);

  // Tracks which existing-image slots the user has cleared in this edit session.
  // Replaces the prior `editingPedal.images[key] = null` prop mutation.
  const [removedExistingImageKeys, setRemovedExistingImageKeys] = useState(() => new Set());

  // When true, edit/reactivate uses the bulk upload UI instead of per-slot tiles.
  const [replaceAllPhotosMode, setReplaceAllPhotosMode] = useState(false);

  // Mirror of firestoreUser kept in a ref so the editingPedal-init effect can read
  // the latest value without re-running every time the user's Firestore doc updates.
  const firestoreUserRef = useRef(firestoreUser);
  useEffect(() => {
    firestoreUserRef.current = firestoreUser;
  }, [firestoreUser]);

  const [orderedFiles, setOrderedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const fileInputRef = useRef(null);
  const priceInputRef = useRef(null);
  const conditionSelectRef = useRef(null);
  const [conditionPopoverDismissed, setConditionPopoverDismissed] = useState(false);
  // Shown only right after the member picks a condition, never because a
  // condition is already stored (edit form, Jason 2026-10-20).
  const [conditionJustPicked, setConditionJustPicked] = useState(false);
  const previewUrlsUnmountRef = useRef([]);
  // Live photo count for addFiles, which reads it after awaiting file copies.
  const orderedFilesCountRef = useRef(0);
  const fileSortableIdsRef = useRef(new WeakMap());
  const nextSortableIdRef = useRef(1);
  /** After a sortable drag, the browser fires a synthetic click; ignore it so we don't open the file picker and break the next drag. */
  const suppressBrowseAfterDragRef = useRef(false);
  const suppressTimerRef = useRef(null);

  const getSortableIdForFile = (file) => {
    let id = fileSortableIdsRef.current.get(file);
    if (!id) {
      id = `create-photo-${nextSortableIdRef.current++}`;
      fileSortableIdsRef.current.set(file, id);
    }
    return id;
  };

  const [isLoading, setIsLoading] = useState(false);
  const useBulkPhotoPipeline = usesBulkPhotoPipeline(editingPedal, replaceAllPhotosMode);
  const [showEffectTypes, setShowEffectTypes] = useState(false);
  
  // Auto-suggestion states
  const [showBrandSuggestions, setShowBrandSuggestions] = useState(false);
  const [showModelSuggestions, setShowModelSuggestions] = useState(false);

  // Reverb-powered suggestions (debounced/cached in the hook). Both the brand
  // and model dropdowns are Reverb-only and canonical — local static/missingPedals
  // data is intentionally NOT shown (it only powers the blur-time missing-pedal
  // check). `loading` drives the "Searching…" row and keeps the dropdown from
  // closing on blur while a request is still in flight.
  const { suggestions: reverbBrandSuggestions, loading: brandLoading } = useReverbBrandSuggestions({
    query: formData.make,
  });
  const displayBrandSuggestions = reverbBrandSuggestions.map((b) => b.make);

  const { suggestions: reverbModelSuggestions, loading: modelLoading } = useReverbProductSuggestions({
    query: formData.model,
    make: formData.make,
  });
  const displayModelSuggestions = reverbModelSuggestions.map((s) => ({
    make: s.make,
    model: s.model,
    // Model field shows the model only; the canonical make is still carried on
    // the object so selecting a row corrects the make field.
    label: s.model,
    fromReverb: true,
    // Catalog facts from the suggestions function (plan §4): effect-type prefill
    // and, later, the photo placeholder. Null when Reverb has none.
    category: s.category ?? null,
    rootCategory: s.rootCategory ?? null,
    imageUrl: s.imageUrl ?? null,
    webLink: s.webLink ?? null,
    slug: s.slug ?? null,
  }));

  // True while either suggestion dropdown is actually on screen (has rows or is
  // mid-search). Used to suppress the missing-pedal banner so it never appears
  // underneath a live list of suggestions the user is still choosing from.
  const suggestionsVisible =
    (showModelSuggestions && (displayModelSuggestions.length > 0 || modelLoading)) ||
    (showBrandSuggestions && (displayBrandSuggestions.length > 0 || brandLoading));
  
  // Missing pedal tracking states
  const [isNewEntry, setIsNewEntry] = useState(false);
  // True once the user picks a brand+model suggestion — a verified-real pedal,
  // so the missing-pedal banner is suppressed regardless of any re-query. Reset
  // the moment they edit make/model.
  const [pedalVerified, setPedalVerified] = useState(false);
  // Armed on model-field blur; the blur-check effect consumes it to decide the
  // banner once Reverb has settled.
  const [awaitingModelBlurCheck, setAwaitingModelBlurCheck] = useState(false);
  const [lastCapitalizedValues, setLastCapitalizedValues] = useState({}); // Track last auto-capitalized values

  // ── Add Pedal wizard ──────────────────────────────────────────────────────
  // The multi-step wizard is the NEW-LISTING experience only. Edit mode
  // (Pedalboard / AdminEditListing pass editingPedal) keeps the single-scroll
  // layout, since the pedal's make/model are already known there.
  const isWizard = !editingPedal;

  const TOTAL_STEPS = 4;
  const STEP_LABELS = ['identify pedal', 'describe your pedal', 'set your price', 'finish & list'];
  const [currentStep, setCurrentStep] = useState(1);

  // Photo reordering is a horizontal drag, and the host pages layer their own
  // horizontal swipe on top (MyPedalsHub swaps pedalboard⟷wants, AddPedalPage
  // swaps add-pedal⟷add-want). Dragging a thumbnail kept firing the page swap
  // and yanking the user out of the edit (Pixel 4a, 2026-08-21). Tell the host
  // when this form owns the horizontal gesture: the entire edit session (all
  // sections visible at once, photos included) or the wizard's photo step.
  //
  // MUST stay below the currentStep declaration. Sitting above it, the
  // dependency array — which is evaluated during render — read currentStep in
  // its temporal dead zone and threw on every render, blanking the whole app
  // (no root ErrorBoundary) on /addpedal, pedal edit, and admin edit listing.
  // Shipped 2026-08-22, caught in production 2026-08-26.
  useEffect(() => {
    if (!onOwnsHorizontalGesture) return undefined;
    onOwnsHorizontalGesture(!!editingPedal || (isWizard && currentStep === 3));
    return () => onOwnsHorizontalGesture(false);
  }, [onOwnsHorizontalGesture, editingPedal, isWizard, currentStep]);
  const prevWizardStepRef = useRef(1);

  // Step changes return to the top of the page. See scrollPageToTop for why this
  // needs more than a scrollTo on iOS. The previous version also called
  // scrollIntoView on the wizard card immediately after scrollTo(0, 0), and the
  // two fought — it landed on the card rather than the top of the page.
  const cancelScrollRef = useRef(null);
  const scrollWizardToTop = () => {
    cancelScrollRef.current?.();
    cancelScrollRef.current = scrollPageToTop();
  };

  useEffect(() => () => {
    cancelScrollRef.current?.();
    revealErrorRef.current?.();
  }, []);

  useEffect(() => {
    if (!isWizard || prevWizardStepRef.current === currentStep) return;
    prevWizardStepRef.current = currentStep;
    scrollWizardToTop();
  }, [currentStep, isWizard]);
  // Field names that failed the current step's Continue check — drives the red
  // highlight on the specific inputs (make/model/condition/price/description/
  // effectTypes). Cleared per field as soon as the user fixes it.
  const [invalidFields, setInvalidFields] = useState([]);
  const isInvalid = (name) => invalidFields.includes(name);

  // Prewarm the Reverb suggestions callable the moment the form mounts, so its
  // container is spinning up before the user starts typing — hiding the cold
  // start instead of paying minInstances to avoid it. Also prewarm the pricing
  // callable (heavy main functions/ codebase, ~6-7s cold start): warming it
  // during Step 1 means the real make/model lookup — which fires the instant a
  // pedal is picked — lands on a hot instance, so the price check is ready and
  // showing by Step 3 instead of failing/blanking on the first cold call.
  useEffect(() => {
    prewarmReverbSuggestions();
    prewarmReverbPricing();
  }, []);

  // Step 1 — single "search for your pedal" box (combined make+model). Selecting
  // a canonical Reverb row locks in the exact make/model the price lookup wants.
  const [pedalQuery, setPedalQuery] = useState('');
  const [showCombinedSuggestions, setShowCombinedSuggestions] = useState(false);
  const [pedalSelected, setPedalSelected] = useState(false);
  const { suggestions: combinedSuggestions, loading: combinedLoading } = useReverbCombinedSuggestions({
    query: pedalQuery,
  });

  // "The lookup settled with zero results" — but Reverb's CSP search is
  // word-based, so a partial token ("thorp") returns nothing even though the
  // finished word ("thorpy") matches. A settled-empty result mid-typing is
  // therefore NOT a real miss. We gate the no-match / manual-entry fallback
  // behind a short idle: it only appears once the query has stayed empty with no
  // further typing, so it never flashes while the user is still finishing a word.
  const combinedQueryTrimmed = pedalQuery.trim();
  const combinedNoMatchRaw =
    (isWizard || isAdminEdit) && !pedalSelected && combinedQueryTrimmed.length >= 2 &&
    !combinedLoading && combinedSuggestions.length === 0;
  const [noMatchSettled, setNoMatchSettled] = useState(false);
  useEffect(() => {
    if (!combinedNoMatchRaw) {
      setNoMatchSettled(false);
      return undefined;
    }
    // Empty AND idle for a beat → treat as a genuine no-match. Any keystroke
    // (query change) or a fresh in-flight fetch resets this before it fires.
    const t = setTimeout(() => setNoMatchSettled(true), 900);
    return () => clearTimeout(t);
  }, [combinedNoMatchRaw, combinedQueryTrimmed]);
  const combinedNoMatch = noMatchSettled;

  // Reverb catalog facts for the pedal picked from the typeahead (null until a
  // suggestion is chosen, or when the member typed an unknown pedal).
  const [reverbCatalog, setReverbCatalog] = useState(null);
  // "I'll add photos later" (plan §4, 2026-10-18): a NEW listing may go live without
  // photos and show a placeholder (Reverb catalog photo when known, else our sketch)
  // until the owner adds real ones. Price, condition and effect type stay required.
  // Edits never use this: once a listing has photos they cannot be removed to none.
  // The tick only has effect while the flow is enabled (featureFlags); with
  // the flag off, skipPhotos is always false and the control is not rendered.
  const [skipPhotosChoice, setSkipPhotos] = useState(false);
  const [showGiveawayPhotoWarning, setShowGiveawayPhotoWarning] = useState(false);
  const skipPhotos = PHOTOS_LATER_ENABLED && skipPhotosChoice;

  // Deep link (`/pedalboard?edit=<id>&step=photos`): once the edit form is on
  // screen, bring the photo section into view instead of the top of the form.
  useEffect(() => {
    if (initialFocus !== 'photos' || !editingPedal) return undefined;
    // The section mounts a beat after the form does, and thumbnails above it
    // shift the layout as they load, so: poll until it exists, jump to it, and
    // re-align once more after the layout has settled.
    let tries = 0;
    let settleTimer = null;
    const scrollTo = () => {
      const el = document.querySelector('.add-pedal-image-section');
      if (!el || typeof el.scrollIntoView !== 'function') return false;
      el.scrollIntoView({ behavior: 'auto', block: 'start' });
      return true;
    };
    const poll = setInterval(() => {
      tries += 1;
      if (scrollTo() || tries > 40) {
        clearInterval(poll);
        settleTimer = setTimeout(scrollTo, 600);
      }
    }, 50);
    return () => { clearInterval(poll); if (settleTimer) clearTimeout(settleTimer); };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- run once per edit session
  }, [initialFocus, editingPedal?.id]);

  // Manual entries (no Reverb suggestion picked) are capitalised on blur so they
  // read like catalog names; a picked suggestion is left exactly as Reverb has it.
  const capitalizeManualField = (field) => {
    if (pedalVerified) return;
    setFormData((prev) => {
      // Never re-case a stored value the member has not changed on an edit
      // (a brand that legitimately starts lowercase must survive a price edit).
      if (editingPedal && prev[field] === editingPedal[field]) return prev;
      const next = titleCaseWords(prev[field] || '');
      return next === prev[field] ? prev : { ...prev, [field]: next };
    });
  };

  const selectCombinedSuggestion = (s) => {
    // Effect-type prefill (plan §4, 2026-10-18): same rule as selectModelSuggestion.
    // Reverb's category pre-ticks the chips only when none are picked yet.
    const prefill = effectTypesForReverbCategory(s.category, s.rootCategory);
    setFormData((prev) => ({
      ...prev,
      make: s.make || '',
      model: s.model || '',
      ...(prefill.length > 0 && (!prev.effectTypes || prev.effectTypes.length === 0)
        ? { effectTypes: prefill }
        : {}),
    }));
    setReverbCatalog({
      slug: s.slug || null,
      category: s.category || null,
      imageUrl: s.imageUrl || null,
      webLink: s.webLink || null,
    });
    setPedalQuery(s.title || `${s.make || ''} ${s.model || ''}`.trim());
    setPedalSelected(true);
    setPedalVerified(true);
    setIsNewEntry(false);
    setShowCombinedSuggestions(false);
  };

  // Manual-entry safety net: while the user fills the Brand/Model fallback
  // fields, quietly re-search the catalog with their combined text. If it
  // matches a real pedal (typo fixed, word completed), surface a tappable
  // "found it" card so they land on the standardized name instead of listing
  // a free-text duplicate — without ever blocking manual entry. The query is
  // '' (hook idles) unless the manual section is actually showing.
  const manualLookupQuery =
    (isWizard || isAdminEdit) && combinedNoMatch && !pedalSelected
      ? `${formData.make || ''} ${formData.model || ''}`.trim()
      : '';
  const { suggestions: manualMatches } = useReverbCombinedSuggestions({
    query: manualLookupQuery,
  });

  // "Not this? Search again" — drop the current pick and reopen the live search.
  const resetPedalSelection = () => {
    setPedalSelected(false);
    setPedalVerified(false);
    // The Reverb catalog facts (placeholder photo, link, category prefill) belong
    // to the pick being dropped; keeping them would hand the next pedal another
    // pedal's catalog photo and effect types.
    setReverbCatalog(null);
    setFormData((prev) => ({ ...prev, make: '', model: '', effectTypes: [] }));
    setShowCombinedSuggestions(true);
  };

  // On a real miss, seed the manual brand/model from a best-effort split (last
  // word = model) so the user rarely has to retype. Runs only once combinedNoMatch
  // is true (post-settle), so the manual fields never flash mid-search.
  useEffect(() => {
    if (!combinedNoMatch) return;
    setFormData((prev) => {
      if (prev.make || prev.model) return prev;
      const q = combinedQueryTrimmed.replace(/\s+/g, ' ');
      const lastSpace = q.lastIndexOf(' ');
      // Capitalised at the seed itself (Jason, 2026-10-19): nothing is blurred on
      // this path, so the on-leave rule never ran and the boxes stayed lowercase.
      return {
        ...prev,
        make: titleCaseWords(lastSpace > 0 ? q.slice(0, lastSpace) : q),
        model: titleCaseWords(lastSpace > 0 ? q.slice(lastSpace + 1) : ''),
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [combinedNoMatch]);

  // Per-step required-field check. Returns the list of missing fields for the
  // given step (empty === valid). The final submit still re-validates everything
  // (incl. images), but this blocks advancing past an incomplete step the same
  // way the single-scroll form blocked submit — with the offending field shown.
  const validateStep = (step) => {
    const missing = [];
    if (step === 1) {
      if (!formData.make.trim()) missing.push({ field: 'make', label: 'Brand' });
      if (!formData.model.trim()) missing.push({ field: 'model', label: 'Model' });
    } else if (step === 2) {
      if (!formData.condition) missing.push({ field: 'condition', label: 'Condition' });
      if (formData.effectTypes.length === 0) {
        missing.push({ field: 'effectTypes', label: 'at least one effect or accessory type' });
      }
    } else if (step === 3) {
      if (!String(formData.price).trim()) missing.push({ field: 'price', label: 'Price' });
    }
    return missing;
  };

  // Continue runs the current step's validation FIRST. If anything's missing we
  // stay on the step, highlight the field(s) red, and surface the error banner —
  // we do NOT advance. Only a clean step moves forward.
  const goNext = () => {
    const missing = validateStep(currentStep);
    if (missing.length > 0) {
      setInvalidFields(missing.map((m) => m.field));
      setErrorMsg(`❌ Please fill in: ${missing.map((m) => m.label).join(', ')}`);
      scrollWizardToTop();
      return;
    }
    setInvalidFields([]);
    setErrorMsg('');
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setCurrentStep((s) => Math.min(TOTAL_STEPS, s + 1));
  };
  const goBack = () => {
    setErrorMsg('');
    setInvalidFields([]);
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setCurrentStep((s) => Math.max(1, s - 1));
  };

  // Clear a field's red highlight the moment it becomes valid (so the user sees
  // the error resolve as they type/select, not only on the next Continue click).
  useEffect(() => {
    if (invalidFields.length === 0) return;
    const stillInvalid = invalidFields.filter((f) => {
      if (f === 'make') return !formData.make.trim();
      if (f === 'model') return !formData.model.trim();
      if (f === 'effectTypes') return formData.effectTypes.length === 0;
      if (f === 'description') return !formData.description.trim();
      if (f === 'condition') return !formData.condition;
      if (f === 'price') return !String(formData.price).trim();
      return false;
    });
    if (stillInvalid.length !== invalidFields.length) {
      setInvalidFields(stillInvalid);
      if (stillInvalid.length === 0) setErrorMsg('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.make, formData.model, formData.effectTypes, formData.description, formData.condition, formData.price]);

  // Show a step's block: in the wizard only the active step is visible (others
  // stay mounted via display:none so the price prefetch keeps warming and photo
  // state survives step changes). In edit mode every block is always visible.
  const stepStyle = (n) => (isWizard && currentStep !== n ? { display: 'none' } : undefined);
  const WIZARD_CARD_STYLE = {
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    padding: '2rem',
    maxWidth: '850px',
    margin: '1rem auto',
    boxSizing: 'border-box',
  };

  // 📜 AUTO-SCROLL: Scroll to success message when it appears
  useEffect(() => {
    if (successMsg && successRef.current) {
      // Small delay to ensure DOM has updated
      const timer = setTimeout(() => {
        successRef.current?.scrollIntoView({ 
          behavior: 'smooth',
          block: 'center'
        });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [successMsg]);


  useEffect(() => {
    if (editingPedal) {
      // Owner edit: prefer the owner's current profile city/state (they may have moved).
      // Admin edit: keep the listing's stored fields — never substitute the admin's profile.
      const fu = firestoreUserRef.current;
      const location = isAdminEdit
        ? (editingPedal.location || '')
        : (fu?.shippingAddress?.city && fu?.shippingAddress?.state
          ? `${fu.shippingAddress.city}, ${fu.shippingAddress.state}`
          : editingPedal.location || '');

      setFormData({
        make: editingPedal.make || '',
        model: editingPedal.model || '',
        condition: editingPedal.condition || '',
        price: editingPedal.price || '',
        location,
        decade: editingPedal.decade || '',
        description: editingPedal.description || '',
        active: editingPedal.active ?? true,
        shipping: true, // Always true - all pedals are willing to ship
        localOnly: false, // Always false - no local-only trades
        offerMode: normalizeOfferMode(editingPedal.offerMode), // missing on older listings = open
        effectTypes: Array.isArray(editingPedal.effectTypes) ? editingPedal.effectTypes : [],
        color: editingPedal.color || '',
        youtubeUrl: editingPedal.youtubeUrl || ''
      });

      setSuccessMsg('');
      setErrorMsg('');
      setRemovedExistingImageKeys(new Set());
      setReplaceAllPhotosMode(false);
      setImages(EMPTY_IMAGES_STATE);
      setOrderedFiles([]);
      setPreviewUrls((prev) => {
        prev.forEach((url) => URL.revokeObjectURL(url));
        return [];
      });

      if (isAdminEdit) {
        const q = `${editingPedal.make || ''} ${editingPedal.model || ''}`.trim();
        setPedalQuery(q);
        setPedalSelected(Boolean(q));
        setPedalVerified(Boolean(q));
        setShowCombinedSuggestions(false);
      }
    }
  }, [editingPedal?.id, isAdminEdit]);

  // Missing-pedal banner: decided ONLY when the user leaves the model field
  // (awaitingModelBlurCheck), and ONLY from Reverb — if Reverb has no canonical
  // suggestion for this make/model, the pedal is unknown. No local DB. We wait
  // for any in-flight Reverb lookup to settle before deciding so we never
  // false-positive on results that simply hadn't arrived yet.
  useEffect(() => {
    if (!awaitingModelBlurCheck) return;

    const make = (formData.make || '').trim();
    const model = (formData.model || '').trim();

    // Verified pick, edit mode, or incomplete name → never flag.
    if (pedalVerified || editingPedal || !make || !model) {
      setIsNewEntry(false);
      setAwaitingModelBlurCheck(false);
      return;
    }

    // Reverb still resolving for this make/model — hold the verdict.
    if (modelLoading) return;

    setIsNewEntry(reverbModelSuggestions.length === 0);
    setAwaitingModelBlurCheck(false);
  }, [awaitingModelBlurCheck, modelLoading, reverbModelSuggestions, formData.make, formData.model, pedalVerified, editingPedal]);

  // Auto-populate location from user's profile for new pedals AND reactivated pedals
  useEffect(() => {
    if (isAdminEdit) return;

    if (firestoreUser?.shippingAddress) {
      const { city, state } = firestoreUser.shippingAddress;
      
      if (city && state) {
        const autoLocation = `${city}, ${state}`;
        
        // Auto-populate for new pedals (not editing)
        if (!editingPedal && !formData.location) {
          console.log('🏠 Auto-populating location for new pedal from user profile:', autoLocation);
          setFormData(prev => ({ ...prev, location: autoLocation }));
        }
        // 🔄 REACTIVATION FIX: Update location for pedals that need reactivation (after trade)
        // This ensures traded pedals get the new owner's location when reactivated
        else if (editingPedal?.needsReactivation && formData.location !== autoLocation) {
          console.log('🔄 Updating location for reactivated pedal to new owner location:', autoLocation);
          setFormData(prev => ({ ...prev, location: autoLocation }));
        }
      }
    }
  }, [firestoreUser, editingPedal, formData.location, isAdminEdit]);

  // (Per-tile blob URL lifecycle is now owned by EditModeImageTile.)

  // Helper function to capitalize first letter of each word
  const capitalizeWords = (str) => {
    // Roman numerals that should be fully capitalized
    const romanNumerals = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII'];
    
    return str
      .toLowerCase()
      .split(' ')
      .map(word => {
        // Check if the word is a Roman numeral
        const upperWord = word.toUpperCase();
        if (romanNumerals.includes(upperWord)) {
          return upperWord;
        }
        // Otherwise, capitalize first letter only
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(' ');
  };

  // Handle blur event for make and model fields to capitalize text and check for missing pedal
  const handleBlur = (e) => {
    const { name, value } = e.target;
    console.log('🔍 handleBlur called:', { name, value });
    
    if (name === 'make' || name === 'model') {
      const trimmedValue = value.trim();
      const capitalizedValue = capitalizeWords(trimmedValue);
      console.log('🔍 Capitalization:', { original: value, trimmed: trimmedValue, capitalized: capitalizedValue });
      
      if (capitalizedValue !== value) {
        console.log('🔍 Updating form data with capitalized value');
        setFormData(prev => ({
          ...prev,
          [name]: capitalizedValue
        }));
      }
      
      // Decide the missing-pedal banner only when leaving the MODEL field. The
      // blur-check effect makes the call from Reverb's results once any in-flight
      // lookup settles (capitalizing above may re-trigger that lookup).
      if (name === 'model') {
        setAwaitingModelBlurCheck(true);
      }
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newVal = type === 'checkbox' ? checked : value;
    
    // Format price input to max 2 decimal places
    if (name === 'price') {
      newVal = formatCurrencyInput(value);
    }
    
    // No need to track manual edits here - we'll detect them in handleBlur
    
    // Editing make/model invalidates any prior verdict, any verified pick, and
    // any pending blur check — clear instantly so a stale banner never lingers
    // while typing. It's re-decided only when the user next leaves the model field.
    if (name === 'make' || name === 'model') {
      setIsNewEntry(false);
      setPedalVerified(false);
      setAwaitingModelBlurCheck(false);
    }

    if (name === 'condition') {
      setConditionPopoverDismissed(false);
    }

    // Handle auto-suggestions for make and model
    if (name === 'make') {
      // Brand suggestions come from the hook (reacts to formData.make). Open the
      // dropdown whenever there's input; the render gates on rows/loading.
      setShowBrandSuggestions(newVal.length > 0);
      // Clear model suggestions when make changes
      setShowModelSuggestions(false);
    }
    
    if (name === 'model') {
      // Reverb suggestions come from the hook above (reacts to formData.model).
      // Open the dropdown whenever there's input; the render gates on
      // displayModelSuggestions actually having rows.
      setShowModelSuggestions(newVal.length > 0);
    }
    
    // Shipping and localOnly are always fixed - no user control
    setFormData((prev) => ({ ...prev, [name]: newVal }));
  };

  // Handle suggestion selection
  const selectBrandSuggestion = (brand) => {
    setFormData(prev => ({ ...prev, make: brand.trim(), model: '' }));
    setShowBrandSuggestions(false);
    setShowModelSuggestions(false);
    // Model was cleared — not a verified pedal yet, and nothing to flag.
    setPedalVerified(false);
    setAwaitingModelBlurCheck(false);
    setIsNewEntry(false);
  };

  const selectModelSuggestion = (suggestion) => {
    // Reverb suggestions carry the canonical make, so picking one also corrects
    // the make field; local fallback suggestions (no make) only set the model.
    // Effect-type prefill (plan §4, 2026-10-18): pre-tick the chips Reverb's
    // category maps to, but only when the member hasn't picked any yet — a
    // suggestion must never wipe a deliberate choice. The chips stay visible and
    // editable, so a wrong second guess ("Overdrive, Boost") costs one tap.
    const prefill = suggestion.fromReverb
      ? effectTypesForReverbCategory(suggestion.category, suggestion.rootCategory)
      : [];
    setFormData(prev => ({
      ...prev,
      model: suggestion.model,
      ...(suggestion.fromReverb && suggestion.make ? { make: suggestion.make } : {}),
      ...(prefill.length > 0 && (!prev.effectTypes || prev.effectTypes.length === 0)
        ? { effectTypes: prefill }
        : {}),
    }));
    // Kept in component state (not yet written to the listing) for the
    // placeholder step that follows.
    setReverbCatalog(suggestion.fromReverb ? {
      slug: suggestion.slug || null,
      category: suggestion.category || null,
      imageUrl: suggestion.imageUrl || null,
      webLink: suggestion.webLink || null,
    } : null);
    setShowModelSuggestions(false);
    // Picking a suggestion means it's a real, canonical Reverb pedal — mark it
    // verified so the banner stays suppressed even though clicking blurs the
    // model field (which would otherwise arm a check).
    setPedalVerified(true);
    setAwaitingModelBlurCheck(false);
    setIsNewEntry(false);
  };

  // Stable callbacks so EditModeImageTile (memo'd) doesn't re-render on every
  // parent render. Blob-URL lifecycle is owned by the tile itself.
  const handlePickImage = useCallback(async (view, file) => {
    // Same Android transient-grant copy + iOS hang guard as addFiles — see
    // src/utils/materializePickedFile.js for both stories.
    const stored = await materializePickedFile(file);
    setImages((prev) => ({ ...prev, [view]: stored }));
  }, []);

  const handleRemoveImage = useCallback((view) => {
    setImages((prev) => ({ ...prev, [view]: null }));
    setRemovedExistingImageKeys((prev) => {
      const next = new Set(prev);
      next.add(view);
      return next;
    });
  }, []);

  const handleReplaceAllPhotos = () => {
    if (isLoading || sellingDisabled) return;
    if (!window.confirm(REPLACE_ALL_PHOTOS_CONFIRM)) return;

    setReplaceAllPhotosMode(true);
    setImages(EMPTY_IMAGES_STATE);
    setRemovedExistingImageKeys(new Set(SLOT_NAMES.filter((k) => editingPedal?.images?.[k])));
    setOrderedFiles([]);
    setPreviewUrls((prev) => {
      prev.forEach((url) => URL.revokeObjectURL(url));
      return [];
    });
    setFormResetKey((k) => k + 1);
    setErrorMsg('');
  };

  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: { delay: 0, tolerance: 5 }
  });
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: { delay: 250, tolerance: 8 }
  });
  const sensors = useSensors(
    ...(isTouchDevice() ? [touchSensor] : [pointerSensor])
  );

  // The ONLY sanctioned way to open the bulk photo picker — a source-text test
  // (photoPickerEntryPoints.test.js) asserts no other call site clicks this
  // input directly. Two protections, both from the iOS "second camera photo
  // never appears" bug (root-caused on-device 2026-09-18):
  //  - The 700ms guard below collapses each human gesture to ONE activation.
  //    The debug log showed a single tap invoking this 2-4x ("+ Add more
  //    photos" bubbling into the box's onClick, doubled by iOS ghost clicks),
  //    and multiple synchronous input.click() calls in one gesture break the
  //    iOS picker session — the eventual capture fires NO change event. THIS
  //    was the actual bug.
  //  - Clearing value at open time is belt-and-braces against iOS naming every
  //    camera capture image.jpg (an unchanged value suppresses change), and is
  //    deliberately NOT in onChange so the Android transient-grant copy in
  //    addFiles always reads a live grant.
  // One picker activation per human gesture, enforced. The on-device debug
  // log (2026-09-18) showed a single tap invoking this 2-4x — the "+ Add more
  // photos" button's click bubbling into the box's onClick, doubled again by
  // iOS ghost clicks — and multiple synchronous input.click() calls in one
  // gesture break the iOS picker session: the eventual camera capture fires
  // NO change event at all. That (not input.value reuse — the log showed the
  // value empty every time) is the root cause of "the second photo never
  // appears". The button also stopPropagation()s; this guard is the backstop
  // for any dispatch path we haven't met yet.
  const lastPickerOpenAtRef = useRef(0);
  const openPhotoPicker = () => {
    const input = fileInputRef.current;
    if (!input) return;
    const now = Date.now();
    if (now - lastPickerOpenAtRef.current < 700) return;
    lastPickerOpenAtRef.current = now;
    input.value = '';
    input.click();
  };

  const addFiles = async (newFiles) => {
    // Snapshot synchronously — the FileList is live and the input is reset below.
    const list = Array.from(newFiles || []).filter((f) => f && typeof f.type === 'string' && f.type.startsWith('image/'));

    // Android WebView hands over Files that are lazy streams on a TRANSIENT
    // content:// permission grant, not materialized copies (iOS/web give real
    // copies). Once the input resets, the grant can lapse — the blob: preview
    // renders as a broken image AND the upload read at submit fails the same
    // way. Copy the bytes NOW, while the grant is alive; on iOS/web this is
    // just a harmless in-memory copy. (Pixel 4a, 2026-08-20)
    //
    // Copied per-file with a timeout (materializePickedFile): on iOS a camera
    // capture's arrayBuffer() can HANG after the WebView was suspended behind
    // the camera. Under the old all-or-nothing Promise.all, one hung read ate
    // the whole batch AND left the input value un-reset, which then suppressed
    // every later capture's change event (see openPhotoPicker). A hang now
    // degrades to the original File for that file only — the same fallback the
    // catch below has always used.
    const toStore = await Promise.all(list.map((f) => materializePickedFile(f)));

    // Both updaters below must stay PURE. The previous version created the
    // blob: URLs and called setPreviewUrls from inside the setOrderedFiles
    // updater. React runs updaters twice under StrictMode (localhost), so every
    // pick appended its preview URLs twice while the file list grew once.
    // previewUrls then held stale extra entries, and because thumbnails read
    // previewUrls[index], every photo added AFTER that showed an earlier
    // photo's picture — "the 5th photo keeps coming up as a repeat"
    // (2026-11-01, desktop Chrome on localhost). Production builds don't
    // double-invoke, so the symptom was dev-only, but the two arrays are a
    // pair and must only ever change together, from the same inputs.
    // The ref gives the room as of the last render, so URLs are not minted
    // for files that will certainly be dropped; the CAP itself is enforced in
    // the updaters, through appendWithCap on BOTH arrays, so two overlapping
    // picks (drop, drop again before the first batch's copies resolve) cannot
    // pass eight photos and the two arrays truncate at the same index
    // (2026-11-01 review).
    const capacity = Math.max(0, 8 - orderedFilesCountRef.current);
    const toAdd = toStore.slice(0, capacity);
    if (toAdd.length > 0) {
      const urls = toAdd.map((f) => URL.createObjectURL(f));
      setOrderedFiles((prev) => appendWithCap(prev, toAdd, 8));
      setPreviewUrls((prev) => {
        const next = appendWithCap(prev, urls, 8);
        // URLs the cap dropped would otherwise leak for the session; revoking
        // is idempotent, so StrictMode's double invocation is harmless.
        urls.slice(next.length - prev.length).forEach((u) => URL.revokeObjectURL(u));
        return next;
      });
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removePhotoAt = (index) => {
    // Revoke outside the updater for the same reason as addFiles above.
    const url = previewUrls[index];
    if (url) URL.revokeObjectURL(url);
    setPreviewUrls((prevUrls) => prevUrls.filter((_, i) => i !== index));
    setOrderedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSortableDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = orderedFiles.map((f) => getSortableIdForFile(f));
    const oldIndex = ids.indexOf(active.id);
    const newIndex = ids.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    setOrderedFiles((prev) => arrayMove(prev, oldIndex, newIndex));
    setPreviewUrls((prev) => arrayMove(prev, oldIndex, newIndex));
  };

  const armSuppressBrowseAfterDrag = () => {
    suppressBrowseAfterDragRef.current = true;
    if (suppressTimerRef.current) clearTimeout(suppressTimerRef.current);
    suppressTimerRef.current = window.setTimeout(() => {
      suppressBrowseAfterDragRef.current = false;
      suppressTimerRef.current = null;
    }, 400);
  };

  const handleCreatePhotosDragEnd = (event) => {
    armSuppressBrowseAfterDrag();
    handleSortableDragEnd(event);
  };

  const handleCreatePhotosDragCancel = () => {
    armSuppressBrowseAfterDrag();
  };

  const createListingSortableIds = useMemo(
    () => orderedFiles.map((f) => getSortableIdForFile(f)),
    [orderedFiles]
  );

  previewUrlsUnmountRef.current = previewUrls;
  orderedFilesCountRef.current = orderedFiles.length;
  useEffect(() => {
    return () => {
      if (suppressTimerRef.current) clearTimeout(suppressTimerRef.current);
      previewUrlsUnmountRef.current.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  // 🛡️ CRITICAL FIX: Prevent accidental form submission when pressing Enter
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
      e.preventDefault();
      console.log('⚠️ Enter key pressed - form submission prevented. Use the submit button instead.');
    }
  };

  /**
   * Reject a save attempt and make sure the user actually sees why.
   *
   * The error banner renders above the form, and in edit mode the form is one
   * long uninterrupted scroll whose only submit control is the floating button
   * pinned to the bottom of the viewport. So the message lands far off screen
   * from where the user just tapped, and a bare scroll gets undone by the iOS
   * WebView — the save reads as a dead button rather than a rejected one.
   * scrollElementIntoView re-asserts across that window.
   */
  const revealError = () => {
    revealErrorRef.current?.();
    revealErrorRef.current = scrollElementIntoView(() => errorRef.current);
  };

  const failValidation = (message) => {
    setErrorMsg(message);
    setIsLoading(false);
    revealError();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 🛡️ CRITICAL FIX: Prevent double submission
    if (isLoading) {
      console.log('⚠️ Form is already submitting - preventing duplicate submission');
      return;
    }
    
    setIsLoading(true);
    setSuccessMsg('');
    setErrorMsg('');

    if (!auth.currentUser) {
      failValidation('You must be logged in to list a pedal.');
      return;
    }

    // Selling-suspension guard. The hook reads from a realtime UserContext
    // snapshot, so a flip mid-form will catch the user here even if the
    // submit button was visible when they started filling out the form.
    if (sellingDisabled && !isAdminEdit) {
      failValidation('Selling, trading & listing are currently paused on your account due to an outstanding recovery invoice. Pay the invoice from your Profile page to restore your account.');
      return;
    }

    // ✅ VERIFICATION CHECK: Ensure user is fully verified before allowing pedal addition
    const currentUser = auth.currentUser;
    
    console.log('🔍 PEDAL FORM VERIFICATION CHECK:', {
      currentUserExists: !!currentUser,
      emailVerified: currentUser?.emailVerified,
      firestoreUserExists: !!firestoreUser,
      phoneVerified: firestoreUser?.phoneVerified,
      isVerified: firestoreUser?.isVerified,
      loading: !firestoreUser && currentUser // Loading if we have a user but no firestore data yet
    });
    
    // Check if we're still loading user data
    if (currentUser && !firestoreUser) {
      failValidation('⏳ Loading user data, please wait...');
      return;
    }
    
    if (!isAdminEdit && (!currentUser?.emailVerified || !firestoreUser?.phoneVerified || !firestoreUser?.isVerified)) {
      console.log('🚫 VERIFICATION FAILED - User blocked from adding pedal');
      failValidation('❌ You must complete both email and phone verification before adding pedals. Please complete your verification in your profile.');
      return;
    }
    
    console.log('✅ VERIFICATION PASSED - User can add pedal');

    // ✅ VALIDATION: Check mandatory fields
    const mandatoryFields = [
      { field: 'make', label: 'Make' },
      { field: 'model', label: 'Model' },
      { field: 'condition', label: 'Condition' },
      { field: 'price', label: 'Price' },
      { field: 'location', label: 'Location' }
      // Description is optional since 2026-10-18 (lightweight listing, plan §4).
    ];
    
    const missingFields = mandatoryFields.filter(({ field }) => {
      const value = formData[field];
      return !value || (typeof value === 'string' && value.trim() === '');
    });
    
    if (missingFields.length > 0) {
      console.log('❌ VALIDATION FAILED: Missing fields', missingFields);
      failValidation(`❌ Please fill in all required fields: ${missingFields.map(({ label }) => label).join(', ')}`);
      return;
    }

    const trimmedDescription = (formData.description || '').trim();
    if (trimmedDescription) {
      const descriptionValidation = validateMessage(trimmedDescription);
      if (!descriptionValidation.isValid) {
        failValidation(`❌ ${descriptionValidation.error}`);
        return;
      }
    }

    // ✅ VALIDATION: Check effect types or accessory types
    if (!formData.effectTypes || formData.effectTypes.length === 0) {
      console.log('❌ VALIDATION FAILED: No effect type or accessory type selected');
      failValidation('❌ Please select at least one effect type or accessory type.');
      return;
    }

    // ✅ VALIDATION: Check if both required images are uploaded
    const requiredImages = ['top', 'back'];
    let missingImages;
    if (useBulkPhotoPipeline) {
      if (orderedFiles.length === 0) {
        missingImages = ['top', 'back'];
      } else if (orderedFiles.length === 1) {
        missingImages = ['back'];
      } else {
        missingImages = [];
      }
    } else {
      missingImages = requiredImages.filter(view => !images[view] && (!editingPedal?.images?.[view] || removedExistingImageKeys.has(view)));
    }

    // Photos are not demanded when (a) a new listing chose "Add photos later", or
    // (b) an existing photo-less listing is edited without adding any: it simply
    // keeps its placeholder. Adding one photo to such a listing still requires two.
    const keepingPlaceholder = editingPedal && hasNoPhotos(editingPedal) && orderedFiles.length === 0;
    // Same rule on create: the box waives photos only while there are NONE.
    // One dropped photo must still be rejected, or a one-photo listing slips
    // through with no placeholder and no photo gate (review, 2026-10-19).
    const noPhotosAtAll = useBulkPhotoPipeline ? orderedFiles.length === 0 : !Object.values(images).some(Boolean);
    const photosLater = skipPhotos && !editingPedal && noPhotosAtAll;
    if (missingImages.length > 0 && !photosLater && !keepingPlaceholder) {
      console.log('❌ VALIDATION FAILED: Missing images', missingImages);
      failValidation(`❌ Please upload both required images: ${missingImages.map(view => view.charAt(0).toUpperCase() + view.slice(1)).join(', ')}`);
      return;
    }

    console.log('✅ ALL BASIC VALIDATIONS PASSED - Proceeding to reactivation check');

    // Reactivation requirements (a new photo + a refreshed description) are
    // enforced ONLY when the user is turning the listing back on — see the check
    // directly below. They used to gate EVERY save, which left a listing that
    // needs reactivation impossible to edit at all, including impossible to
    // deactivate: the block demanded new photos before it would let you switch
    // the listing off.

    // ✅ VALIDATION: Check if pedal needs reactivation AND user is trying to activate it
    if (editingPedal?.needsReactivation && formData.active && !editingPedal.active) {
      // User is trying to activate a pedal that needs reactivation
      const hasUpdatedImages = hasNewSessionImages(useBulkPhotoPipeline, orderedFiles, images);
      const hasUpdatedDescription = formData.description !== editingPedal.description;
      
      console.log('🔄 Reactivation validation:', {
        hasUpdatedImages,
        hasUpdatedDescription,
        imagesState: images,
        originalDescription: editingPedal.description,
        newDescription: formData.description
      });
      
      if (!hasUpdatedImages && !hasUpdatedDescription) {
        failValidation('❌ This pedal requires new images and description updates before reactivation. Please upload at least one new image and update the description.');
        return;
      }

      if (!hasUpdatedImages) {
        failValidation('❌ This pedal requires new images before reactivation. Please upload at least one new image.');
        return;
      }

      if (!hasUpdatedDescription) {
        failValidation('❌ This pedal requires description updates before reactivation. Please update the description.');
        return;
      }
    }

    // Check if this is the user's first listing — gates firstMatchEligible.
    // (Previously also gated a post-listing referral modal, removed 2026-08-20
    // as one popup too many; the referral link lives on the Profile page.)
    let hadNoListings = false;
    if (!editingPedal) {
      try {
        const listingsQuery = query(
          collection(db, 'listings'),
          where('ownerId', '==', auth.currentUser.uid),
          where('active', '==', true)
        );
        const snap = await getDocs(listingsQuery);
        hadNoListings = snap.empty;
      } catch (err) {
        console.error('Could not check existing listings:', err);
      }
    }

    try {
      // Get Firebase Storage instance
      const storage = getStorage();
      if (!storage) {
        throw new Error('Firebase Storage is not initialized. Please refresh the page and try again.');
      }
      
      const imageUrls = (editingPedal && !replaceAllPhotosMode && editingPedal.images)
        ? { ...editingPedal.images }
        : {};

      const imagesForPipeline = useBulkPhotoPipeline
        ? orderedFilesToImagesMap(orderedFiles)
        : images;

      // 🚀 IMAGE COMPRESSION: Compress images before upload for better performance
      // Detect if user is on mobile/iOS for adjusted compression settings
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      // Compression settings - Web Workers disabled to avoid Event errors
      // Web Workers can fail with CORS/CSP issues causing generic Event errors
      // OPTIMIZED: Reduced quality/size for 30-40% faster compression (still looks great!)
      const compressionOptions = {
        maxSizeMB: isMobile ? 0.8 : 1.2,          // Lowered to prevent issues in Brave and similar browsers
        maxWidthOrHeight: isMobile ? 1600 : 1800,  // Reduced max dimension to lower decoded memory
        useWebWorker: false, // DISABLED - was causing Event errors instead of proper Error objects
        quality: isMobile ? 0.75 : 0.78,      // Slightly lower quality (negligible visual difference)
        fileType: 'image/jpeg',
        alwaysKeepResolution: false,
        initialQuality: 0.75
      };
      
      console.log(`📱 Device type: ${isMobile ? 'Mobile' : 'Desktop'}, Compression target: ${compressionOptions.maxSizeMB}MB`);

      // 🚀 PERFORMANCE FIX: Upload all images in parallel instead of sequentially
      const uploadPromises = Object.entries(imagesForPipeline).map(async ([view, file]) => {
        if (file) {
          let compressionFailed = false; // Track if compression failed for error logging
          
          try {
            // Validate file before processing
            if (!file.type || (!file.type.startsWith('image/') && file.type !== 'application/octet-stream')) {
              throw new Error(`Invalid file type for ${view}: ${file.type || 'unknown'}. Please use JPG or PNG images.`);
            }
            
            // Calculate original size FIRST (needed for validation)
            const originalSizeMB = file.size / 1024 / 1024;
            
            // Enforce reasonable size limit for uploads (20MB)
            const MAX_UPLOAD_SIZE_MB = 20;
            if (originalSizeMB > MAX_UPLOAD_SIZE_MB) {
              throw new Error(
                `${view} image is too large (${originalSizeMB.toFixed(1)}MB). ` +
                `Maximum file size is ${MAX_UPLOAD_SIZE_MB}MB. ` +
                `Please reduce the image size using your camera settings or a photo editor.`
              );
            }
            
            // Check auth before upload
            if (!auth.currentUser || !auth.currentUser.uid) {
              throw new Error('User authentication lost. Please refresh the page and try again.');
            }
            
            // Compress image before upload
            const originalSize = originalSizeMB.toFixed(2);
            console.log(`📸 Processing ${view} image - Original: ${originalSize}MB, Type: ${file.type}`);
            
            let compressedFile;
            let compressionFailed = false; // Track if we're uploading uncompressed file
            
            // ALWAYS compress to ensure HEIC/HEIF files are converted to JPEG
            // (HEIC files from iPhones don't display in Chrome/Firefox, only Safari)
            try {
                console.log(`🔄 Starting compression for ${view} - ${originalSizeMB.toFixed(2)}MB`);
                
                // Wrap compression to catch Event errors
                const compressionPromise = new Promise(async (resolve, reject) => {
                  try {
                    const result = await imageCompression(file, compressionOptions);
                    resolve(result);
                  } catch (err) {
                    // Convert Event errors to proper Error objects
                    if (err instanceof Event) {
                      reject(new Error(`Compression library error: ${err.type || 'unknown event'}`));
                    } else {
                      reject(err);
                    }
                  }
                });
                
                const timeoutPromise = new Promise((_, reject) => 
                  setTimeout(() => reject(new Error('Compression timeout after 30 seconds')), 30000)
                );
                
                compressedFile = await Promise.race([compressionPromise, timeoutPromise]);
                const compressedSize = (compressedFile.size / 1024 / 1024).toFixed(2);
                const savings = ((1 - compressedFile.size / file.size) * 100).toFixed(1);
                console.log(`✅ Compressed ${view} image - New: ${compressedSize}MB (${savings}% smaller)`);
              } catch (compressionError) {
                console.error(`❌ Image compression failed for ${view}:`, compressionError);
                console.error('Compression error type:', compressionError.constructor?.name);
                
                // Fallback: Upload original if <= 10MB, server will compress it
                if (originalSizeMB <= 10) {
                  console.warn(`⚠️ Compression failed - uploading original ${view} image (${originalSizeMB.toFixed(2)}MB). Server will compress it.`);
                  compressedFile = file;
                  compressionFailed = true;
                } else {
                  throw new Error(
                    `Image compression failed and original file is too large (${originalSizeMB.toFixed(2)}MB). ` +
                    `Please reduce the file size using a photo editor or your camera settings before uploading.`
                  );
                }
              }
            
            // Upload to storage
            const uploadUid = isAdminEdit && editingPedal?.ownerId ? editingPedal.ownerId : auth.currentUser.uid;
            const storageRef = ref(storage, `images/${uploadUid}/${Date.now()}_${view}.jpg`);
            if (compressionFailed) {
              console.log(`⬆️ Uploading ${view} ORIGINAL image (compression failed) - Size: ${(file.size / 1024 / 1024).toFixed(2)}MB`);
            } else {
              console.log(`⬆️ Uploading ${view} compressed image to storage...`);
            }
            
            try {
              await uploadBytes(storageRef, compressedFile);
              console.log(`✅ ${view} image uploaded, getting download URL...`);
            } catch (uploadError) {
              console.error(`❌ Storage upload failed for ${view}:`, uploadError);
              console.error('Upload error details:', {
                code: uploadError.code,
                message: uploadError.message,
                name: uploadError.name,
                serverResponse: uploadError.serverResponse
              });
              
              // Check for App Check or authentication related errors
              if (uploadError.code === 'storage/unauthorized') {
                throw new Error('Upload permission denied. Please: 1) Refresh the page and try again 2) If that doesn\'t work, log out and log back in 3) Try a different browser if issue persists. Contact support if this continues.');
              } else if (uploadError.message?.includes('AppCheck') || 
                         uploadError.message?.includes('app-check') ||
                         uploadError.message?.includes('403')) {
                throw new Error('Upload blocked by security check. This can happen with strict browser privacy settings or ad blockers. Please: 1) Temporarily disable browser extensions 2) Clear cache/cookies 3) Refresh and try again. If issue persists, contact support.');
              }
              
              // Re-throw to be caught by outer catch with proper error details
              throw uploadError;
            }
            
            let url;
            try {
              url = await getDownloadURL(storageRef);
              console.log(`✅ ${view} image URL obtained: ${url.substring(0, 50)}...`);
            } catch (urlError) {
              console.error(`❌ Failed to get download URL for ${view}:`, urlError);
              throw new Error(`Image uploaded but failed to get URL: ${urlError.message || 'Unknown error'}. Please refresh and try again.`);
            }
            
            return { view, url };
          } catch (imageError) {
            console.error(`❌ Error processing/uploading ${view} image:`, imageError);
            console.error('Error details:', {
              name: imageError?.name,
              message: imageError?.message,
              code: imageError?.code,
              type: imageError?.constructor?.name,
              customData: imageError?.customData,
              stack: imageError?.stack,
              isEvent: imageError instanceof Event,
              eventType: imageError?.type
            });
            
            // Extract more details from Firebase Storage errors
            let errorMessage = 'Unknown error';
            let errorCode = 'UNKNOWN';
            
            // Check if this is a generic Event object (common with compression library failures)
            if (imageError instanceof Event) {
              errorCode = 'COMPRESSION_EVENT_ERROR';
              errorMessage = 'Image compression failed due to browser limitation or corrupted file. Try: 1) Use a different image 2) Open the image in an editor and save a new copy 3) Take a new photo if possible';
              console.error('Caught Event object - likely compression library failure');
            } else if (imageError?.code) {
              // Firebase error codes
              errorCode = imageError.code;
              switch (imageError.code) {
                case 'storage/unauthorized':
                  errorMessage = 'You do not have permission to upload images. Please refresh and try again.';
                  break;
                case 'storage/canceled':
                  errorMessage = 'Upload was cancelled.';
                  break;
                case 'storage/unknown':
                  errorMessage = `Storage error: ${imageError.message || 'Unknown storage issue'}`;
                  break;
                case 'storage/object-not-found':
                  errorMessage = 'Storage location not found.';
                  break;
                case 'storage/bucket-not-found':
                  errorMessage = 'Storage bucket not configured.';
                  break;
                case 'storage/quota-exceeded':
                  errorMessage = 'Storage quota exceeded. Please contact support.';
                  break;
                case 'storage/unauthenticated':
                  errorMessage = 'Authentication expired. Please refresh the page and try again.';
                  break;
                case 'storage/retry-limit-exceeded':
                  errorMessage = 'Upload failed after multiple retries. Check your internet connection.';
                  break;
                default:
                  errorMessage = imageError.message || `Storage error: ${errorCode}`;
              }
            } else if (imageError?.message) {
              errorMessage = imageError.message;
              errorCode = imageError.name || 'ERROR';
            }
            
            // Log image-specific error to Firestore with enhanced details
            logError('AddPedalForm - Image Upload', imageError, {
              imageView: view,
              fileName: file.name,
              fileSize: file.size,
              fileType: file.type,
              originalFileName: file.name,
              compressionSettings: compressionOptions,
              compressionFailed: compressionFailed,
              errorCode,
              errorMessage,
              isEventObject: imageError instanceof Event,
              eventType: imageError?.type,
              errorType: imageError?.constructor?.name,
              // Storage-specific data
              storageErrorDetails: {
                code: imageError?.code,
                message: imageError?.message,
                customData: imageError?.customData,
                serverResponse: imageError?.serverResponse
              }
            });
            
            throw new Error(`Failed to upload ${view} image: ${errorMessage}. Please try a different image or contact support.`);
          }
        } else if (editingPedal && removedExistingImageKeys.has(view)) {
          return { view, url: null }; // Mark for deletion
        }
        return null;
      });

      // Wait for all uploads to complete - use allSettled so one failure doesn't cancel others
      const uploadResults = await Promise.allSettled(uploadPromises);
      
      // Process results and collect errors
      const failedUploads = [];
      uploadResults.forEach((result, index) => {
        if (result.status === 'fulfilled' && result.value) {
          if (result.value.url) {
            imageUrls[result.value.view] = result.value.url;
          } else if (result.value.url === null) {
            delete imageUrls[result.value.view];
          }
        } else if (result.status === 'rejected') {
          const view = Object.keys(imagesForPipeline)[index];
          if (imagesForPipeline[view]) { // Only count as failed if there was actually an image to upload
            failedUploads.push({ view, error: result.reason });
            console.error(`❌ Upload failed for ${view}:`, result.reason);
          }
        }
      });
      
      // If ANY uploads failed, throw error with details
      if (failedUploads.length > 0) {
        const failedViews = failedUploads.map(f => f.view).join(', ');
        const firstError = failedUploads[0].error?.message || failedUploads[0].error || 'Unknown error';
        throw new Error(`Failed to upload ${failedUploads.length} image(s) (${failedViews}): ${firstError}`);
      }

      const ACCESSORY_TYPES = ['Power Supply', 'Pedalboard', 'Other Accessory'];
      const hasEffectType = formData.effectTypes.some(t => !ACCESSORY_TYPES.includes(t));
      const category = hasEffectType ? 'pedal' : 'accessory';

      const cleanYoutubeUrl = formData.youtubeUrl?.trim() || '';
      const ytValid = !cleanYoutubeUrl || /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([a-zA-Z0-9_-]{11})/.test(cleanYoutubeUrl);

      const payload = {
  ...formData,
  // Manual entries saved with catalog-style casing (see capitalizeManualField);
  // a picked Reverb name, or an unchanged stored name on an edit, is left alone.
  make: (pedalVerified || (editingPedal && formData.make === editingPedal.make)) ? formData.make : titleCaseWords(formData.make),
  model: (pedalVerified || (editingPedal && formData.model === editingPedal.model)) ? formData.model : titleCaseWords(formData.model),
  ownerId: isAdminEdit && editingPedal?.ownerId ? editingPedal.ownerId : auth.currentUser.uid,
  price: Number(formData.price),
  images: imageUrls,
  // Placeholder only for a new photo-less listing; an edit that has (or keeps)
  // real photos clears any lingering placeholder so nothing stale is served.
  ...(!editingPedal && skipPhotos && hasNoPhotos({ images: imageUrls })
    ? { placeholderImage: buildPlaceholderImage(reverbCatalog) }
    : {}),
  ...(editingPedal && !hasNoPhotos({ images: imageUrls }) && editingPedal.placeholderImage
    ? { placeholderImage: deleteField() }
    : {}),
  shipping: true,
  localOnly: false,
  category,
  youtubeUrl: ytValid ? cleanYoutubeUrl : ''
};

      // ✅ Clear needsReactivation flag if pedal is being reactivated with proper updates
      if (editingPedal?.needsReactivation && formData.active) {
        // Check if any new images were uploaded (comparing with original images)
        const hasUpdatedImages = useBulkPhotoPipeline
          ? Object.values(imageUrls).some((url) => !!url)
          : Object.entries(images).some(([key, file]) => {
              if (file !== null) {
                return true;
              }
              if (removedExistingImageKeys.has(key) && imageUrls[key]) {
                return true;
              }
              return false;
            });
        
        const hasUpdatedDescription = formData.description !== editingPedal.description;
        
        console.log('🔄 Reactivation check:', {
          hasUpdatedImages,
          hasUpdatedDescription,
          originalDescription: editingPedal.description,
          newDescription: formData.description,
          imagesState: images,
          originalImages: editingPedal.images
        });
        
        // Both new images AND description updates are required for reactivation
        if (hasUpdatedImages && hasUpdatedDescription) {
          // 🔄 NEW REACTIVATION LOGIC: Create a brand new pedal instead of updating existing one
          console.log('✅ Reactivation requirements met - creating NEW pedal with fresh ID');
          
          try {
            // Create new pedal payload
            const newPedalPayload = {
              ...payload,
              needsReactivation: false,
              reactivatedAt: new Date().toISOString(),
              active: true,
              originalPedalId: editingPedal.id, // Reference to the old pedal for tracking
              createdAt: serverTimestamp(), // Fresh creation date for "Browse Pedals"
              listedAt: serverTimestamp()
            };
            
            // addDoc rejects a deleteField() sentinel (only update/merge accept
            // it); a reactivated listing that gained photos simply omits the field.
            if (newPedalPayload.placeholderImage && typeof newPedalPayload.placeholderImage.url !== 'string') {
              delete newPedalPayload.placeholderImage;
            }
            // Create the new pedal
            const newPedalRef = await addDoc(collection(db, 'listings'), newPedalPayload);
            console.log('✅ New pedal created with ID:', newPedalRef.id);
            
            // Hide the old pedal from pedalboards (but keep for trade history)
            await updateDoc(doc(db, 'listings', editingPedal.id), {
              hiddenFromPedalboard: true,
              replacedByPedalId: newPedalRef.id,
              replacedAt: serverTimestamp()
            });
            console.log('✅ Old pedal hidden from pedalboards, trade history preserved');
            
            // Track analytics for recommendation engine
            try {
              await trackPedalAdded({
                id: newPedalRef.id,
                ...newPedalPayload
              });
              console.log('✅ Reactivated pedal analytics tracked');
            } catch (error) {
              console.error('❌ Analytics tracking failed:', error);
            }
            
            setSuccessMsg('✅ Pedal reactivated as a new listing!');
            onFinishEdit?.();
          } catch (reactivationError) {
            console.error('❌ REACTIVATION FAILED:', reactivationError);
            setErrorMsg(`❌ Failed to reactivate pedal: ${reactivationError.message || 'Unknown error'}. Please try again or contact support if the problem persists.`);
            revealError();
          } finally {
            setIsLoading(false);
          }
          return; // Exit early - we're not updating the old pedal
        } else {
          console.log('❌ Reactivation failed - missing requirements');
          if (!hasUpdatedImages && !hasUpdatedDescription) {
            failValidation('❌ This pedal requires BOTH new images AND description updates before reactivation. Please upload at least one new image AND update the description.');
          } else if (!hasUpdatedImages) {
            failValidation('❌ This pedal requires new images before reactivation. Please upload at least one new image.');
          } else if (!hasUpdatedDescription) {
            failValidation('❌ This pedal requires description updates before reactivation. Please update the description.');
          } else {
            setIsLoading(false);
          }
          return;
        }
      }



      // Price drop metadata — seller edit only; admin edits must not touch these
      // fields. The rules (including the relist-after-trade carve-out) live in
      // src/utils/priceDropPolicy.js so they can be tested without the form.
      if (editingPedal && !isAdminEdit) {
        const priceChange = resolvePriceDropChange(editingPedal, formData);

        if (priceChange.clearDropMetadata) {
          payload.previousPrice = deleteField();
          payload.priceDroppedAt = deleteField();
        }
        if (priceChange.setDrop) {
          payload.priceDroppedAt = serverTimestamp();
        }
        if (priceChange.setPreviousPrice != null) {
          payload.previousPrice = priceChange.setPreviousPrice;
        }
        if (priceChange.refreshListedAt) {
          payload.listedAt = serverTimestamp();
        }
        if (priceChange.markRelisted) {
          // Consumes the trade/purchase tombstone so the seller's NEXT price
          // cut on this listing is treated as a real drop again.
          payload.relistedAt = serverTimestamp();
        }
      }

      // Set only for a confirmed NEW listing (not edits or reactivations).
      let listingPrompt = null;
      if (editingPedal) {
        await updateDoc(doc(db, 'listings', editingPedal.id), payload);
        setSuccessMsg('✅ Pedal updated successfully!');
        onFinishEdit?.();
      } else {
        const newPedalRef = await addDoc(collection(db, 'listings'), {
          ...payload,
          createdAt: serverTimestamp(),
          listedAt: serverTimestamp()
        });
        setSuccessMsg('');
        await new Promise(r => setTimeout(r, 0));
        setSuccessMsg('✅ Pedal listed successfully!');
        // Native "turn on notifications" pop-up after a confirmed new listing
        // (2026-10-07). Fire-and-forget: the listing's success never waits on
        // it, and a prompt failure resolves as "not opened".
        listingPrompt = Promise.resolve()
          .then(() => maybePromptForPush('list_pedal'))
          .catch(() => ({ opened: false }));
        
        // Track analytics for recommendation engine
        try {
          await trackPedalAdded({
            id: newPedalRef.id,
            ...payload
          });
          console.log('✅ Pedal analytics tracked');
        } catch (error) {
          console.error('❌ Analytics tracking failed:', error);
        }
        
        // Cloud Function will handle match creation
        console.log('✅ Pedal added - Cloud Function will check for matches');
        
        // Track missing pedals only if it's truly new (not in suggestions)
        if (isNewEntry && formData.make && formData.model) {
          try {
            await trackMissingPedal(formData.make, formData.model, auth.currentUser.uid);
            console.log('🆕 Missing pedal tracked on form submission');
          } catch (error) {
            console.error('❌ Missing pedal tracking failed:', error);
          }
        }
        
        // 🧹 COMPLETE FORM RESET: Clear all form data and state for new pedal submissions
        setFormData({
          make: '', model: '', condition: '', price: '',
          location: '', decade: '', description: '', active: true,
          shipping: true, localOnly: false, offerMode: 'open', // Always true/false; new listings start open
          effectTypes: [], color: ''
        });
        
        // Clear images and force file input DOM recreation
        setImages({ top: null, back: null, upper: null, lower: null, detail1: null, detail2: null, detail3: null, detail4: null });
        previewUrls.forEach((url) => URL.revokeObjectURL(url));
        setOrderedFiles([]);
        setPreviewUrls([]);
        setFormResetKey(prev => prev + 1);
        
        // Clear suggestion states
        setShowBrandSuggestions(false);
        setShowModelSuggestions(false);
        setPedalQuery('');
        setPedalSelected(false);
        setShowCombinedSuggestions(false);
        setPedalVerified(false);
        
        // Clear status states
        setIsNewEntry(false);
        setShowEffectTypes(false);
        setSkipPhotos(false);
        setReverbCatalog(null);

        // Back to step 1 so the wizard is ready for another listing instead of
        // sitting on the (now-empty) last step. The existing currentStep
        // effect scrolls the wizard back to the top when this fires.
        setCurrentStep(1);

        // Re-populate location after clearing form
        if (firestoreUser?.shippingAddress?.city && firestoreUser?.shippingAddress?.state) {
          const autoLocation = `${firestoreUser.shippingAddress.city}, ${firestoreUser.shippingAddress.state}`;
          setTimeout(() => {
            setFormData(prev => ({ ...prev, location: autoLocation }));
          }, 100); // Small delay to ensure form state is cleared first
        }
      }

      if (hadNoListings && !editingPedal && !isAdminEdit) {
        // Mark this user as eligible for the first-match modal. Written here —
        // after a confirmed successful addDoc — so it never fires on a failed
        // or partial submission.
        try {
          await updateDoc(doc(db, 'users', auth.currentUser.uid), { firstMatchEligible: true });
        } catch (e) {
          console.error('[FirstMatch] Failed to set firstMatchEligible:', e);
        }
      }

      // Same 5 s auto-hide as before; held only while the pop-up this listing
      // actually opened is on screen (utils/listingBannerTimer.js).
      armBannerAroundPrompt(
        listingPrompt,
        successTimeoutRef,
        () => setTimeout(() => setSuccessMsg(''), 5000),
        (t) => clearTimeout(t)
      );

      setTimeout(() => {
        successRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 150);
    } catch (err) {
      console.error('❌ Pedal submission error:', err);
      console.error('❌ Error details - Code:', err.code, '| Message:', err.message, '| Name:', err.name, '| Full error:', JSON.stringify(err, Object.getOwnPropertyNames(err)));
      
      // 📊 LOG ERROR TO FIRESTORE for admin review (doesn't require user to send console logs)
      logError('AddPedalForm', err, {
        formData: {
          make: formData.make,
          model: formData.model,
          condition: formData.condition,
          price: formData.price,
          hasImages: Object.entries(
            useBulkPhotoPipeline ? orderedFilesToImagesMap(orderedFiles) : images
          ).filter(([, file]) => file).map(([view]) => view),
          effectTypes: formData.effectTypes
        },
        editingPedal: !!editingPedal,
        replaceAllPhotosMode,
        isReactivation: editingPedal?.needsReactivation || false
      });
      
      // Provide specific, user-friendly error messages based on error type
      let errorMessage = '❌ Failed to submit pedal. ';
      let errorCode = err.code || err.name || 'UNKNOWN';
      
      if (err.code === 'permission-denied') {
        errorMessage += 'You don\'t have permission to perform this action. Please make sure you\'re logged in and try again.';
        errorCode = err.code;
      } else if (err.code === 'unauthenticated') {
        errorMessage += 'Your session has expired. Please log out and log back in.';
        errorCode = err.code;
      } else if (err.code === 'unavailable' || err.message?.includes('Failed to fetch')) {
        errorMessage += 'Network connection issue. Please check your internet connection and try again.';
        errorCode = err.code || 'NETWORK_ERROR';
      } else if (err.code === 'storage/unauthorized') {
        errorMessage += 'Failed to upload images. Please check that your images are valid JPG or PNG files under 5MB each.';
        errorCode = err.code;
      } else if (err.code === 'storage/quota-exceeded') {
        errorMessage += 'Storage quota exceeded. Please contact support.';
        errorCode = err.code;
      } else if (err.message?.includes('storage') || err.message?.includes('upload')) {
        errorMessage += 'Failed to upload images. Please try using smaller image files (under 5MB) and make sure they are JPG or PNG format.';
        errorCode = err.code || 'STORAGE_ERROR';
      } else if (err.message?.includes('document') || err.message?.includes('Firestore')) {
        errorMessage += 'Database error. Please try again. If this persists, contact support.';
        errorCode = err.code || 'DB_ERROR';
      } else if (err.message) {
        errorMessage += 'Please try again. If this continues, contact support.';
        errorCode = err.code || err.name || 'ERROR';
      } else {
        errorMessage += 'An unexpected error occurred. Please try again or contact support.';
        errorCode = 'UNKNOWN_ERROR';
      }
      
      // Always append error code and snippet of error message for troubleshooting
      errorMessage += ` (Error code: ${errorCode}`;
      if (err.message && err.message.length > 0) {
        const messageSnippet = err.message.substring(0, 100);
        errorMessage += ` | Details: ${messageSnippet}${err.message.length > 100 ? '...' : ''}`;
      }
      errorMessage += ')';
      
      setErrorMsg(errorMessage);
      revealError();
    } finally {
      setIsLoading(false);
    }
  };

  // Active/inactive switch. Rendered at the top when editing, at the end of
  // the new-listing wizard.
  // Offer mode (plan §5, Jason 2026-10-20): three switches that act as one choice.
  // Turning on Trade only or Sale only turns the others off; turning the chosen
  // one off falls back to Any offer. Under the description on Add Pedal, at the
  // top beside the active switch on edit.
  const setOfferMode = (mode) => setFormData((prev) => ({ ...prev, offerMode: mode }));
  const offerModeRow = (mode, colorOn, compact = false) => {
    const on = (formData.offerMode || 'open') === mode;
    return (
      <div
        key={mode}
        className="offer-mode-row"
        onClick={compact ? () => setOfferMode(on ? 'open' : mode) : undefined}
        style={{ display: 'flex', alignItems: 'center', gap: compact ? '0.5rem' : '0.6rem', padding: compact ? '0.4rem 0.75rem' : '0.5rem 0.75rem', borderRadius: '10px', backgroundColor: on ? colorOn.bg : '#F9FAFB', border: `1px solid ${on ? colorOn.border : '#E5E7EB'}`, transition: 'background-color 0.2s ease', flex: compact ? '1 1 0' : undefined, minWidth: 0, cursor: compact ? 'pointer' : undefined }}
      >
        <button
          type="button"
          role="switch"
          aria-checked={on}
          aria-label={OFFER_MODE_LABELS[mode]}
          className="offer-mode-switch"
          onClick={(e) => { e.stopPropagation(); setOfferMode(on ? 'open' : mode); }}
          style={{ width: '44px', height: '24px', borderRadius: '12px', border: 'none', backgroundColor: on ? colorOn.knob : '#d1d5db', position: 'relative', cursor: 'pointer', transition: 'background-color 0.2s ease', flexShrink: 0, padding: 0 }}
        >
          <span style={{ position: 'absolute', top: '2px', left: on ? '22px' : '2px', width: '20px', height: '20px', borderRadius: '50%', backgroundColor: 'white', transition: 'left 0.2s ease', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
        </button>
        <div style={{ textAlign: 'left', minWidth: 0 }}>
          <span className="toggle-title offer-mode-label" style={{ fontSize: compact ? '0.85rem' : '0.95rem', fontWeight: '600', color: on ? colorOn.text : '#6b7280', whiteSpace: 'nowrap' }}>{OFFER_MODE_LABELS[mode]}</span>
          {!compact && (
            <span className="toggle-subtitle" style={{ display: 'block', fontSize: '0.8rem', color: on ? colorOn.text : '#9ca3af' }}>{OFFER_MODE_DESCRIPTIONS[mode]}</span>
          )}
        </div>
      </div>
    );
  };
  const OFFER_MODE_COLORS = {
    open: { bg: 'rgba(22, 101, 52, 0.08)', border: '#BBE5C8', knob: '#166534', text: '#166534' },
    trade: { bg: '#FBF3DF', border: '#E5D9B6', knob: '#C89F3C', text: '#7C5A12' },
    sell: { bg: '#EEF5FA', border: '#CFE3F0', knob: '#5A9ECB', text: '#2F6B94' },
  };
  // Add Pedal: full rows with the explanation under the description.
  const offerModeToggles = (
    <div className="offer-mode-container" style={{ width: '100%', margin: '0 0 1rem 0', padding: 0, textAlign: 'left' }}>
      <label className="font-bold block" style={{ marginBottom: '0.4rem' }}>Offers you'll accept:</label>
      <div style={{ display: 'grid', gap: '0.5rem' }}>
        {offerModeRow('open', OFFER_MODE_COLORS.open)}
        {offerModeRow('trade', OFFER_MODE_COLORS.trade)}
        {offerModeRow('sell', OFFER_MODE_COLORS.sell)}
      </div>
    </div>
  );
  // Edit: the member already read the explanation when listing, so one
  // segmented band, the same control as the Browse page tabs (Jason,
  // 2026-10-20): three equal segments, the chosen one filled gold, full width of
  // the active box above on every screen size.
  const offerModeTogglesCompact = (() => {
    const modes = ['open', 'trade', 'sell'];
    const idx = Math.max(0, modes.indexOf(formData.offerMode || 'open'));
    return (
      <div className="offer-mode-compact" style={{ maxWidth: '850px', margin: '0 auto 1rem auto', padding: 0 }}>
        {/* A sliding thumb inside an inset track, so it reads as a control that
            moves rather than three buttons (Jason, 2026-10-20). */}
        <div role="radiogroup" aria-label="Offers you'll accept" style={{ position: 'relative', display: 'flex', width: '100%', backgroundColor: 'white', borderRadius: '25px', padding: '4px', boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #E5E7EB', boxSizing: 'border-box' }}>
          <div
            aria-hidden="true"
            style={{ position: 'absolute', top: '4px', bottom: '4px', left: '4px', width: 'calc((100% - 8px) / 3)', borderRadius: '25px', backgroundColor: '#C89F3C', boxShadow: '0 2px 6px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.35)', transform: `translateX(${idx * 100}%)`, transition: 'transform 0.22s ease' }}
          />
          {modes.map((mode) => {
            const on = idx === modes.indexOf(mode);
            return (
              <button
                key={mode}
                type="button"
                role="radio"
                aria-checked={on}
                onClick={() => setOfferMode(mode)}
                style={{ position: 'relative', zIndex: 1, flex: '1 1 0', minWidth: 0, padding: '0.5rem 0.5rem', borderRadius: '25px', border: 'none', fontWeight: '700', fontSize: '0.95rem', fontFamily: 'Nunito', cursor: 'pointer', transition: 'color 0.2s', backgroundColor: 'transparent', color: on ? 'white' : '#9CA3AF', whiteSpace: 'nowrap' }}
              >
                {OFFER_MODE_LABELS[mode]}
              </button>
            );
          })}
        </div>
      </div>
    );
  })();

  const activeToggle = (
    <div 
      className="add-pedal-form-container active-toggle-container"
      style={{
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      padding: '0.5rem 2rem',
      backgroundColor: formData.active ? 'rgba(22, 101, 52, 0.08)' : '#f3f4f6',
      borderRadius: '12px',
      maxWidth: '850px',
      margin: '0 auto 1rem auto',
      transition: 'background-color 0.2s ease'
    }}>
      <button
        type="button"
        role="switch"
        aria-checked={formData.active}
        onClick={() => setFormData(prev => ({ ...prev, active: !prev.active }))}
        style={{
          width: '44px',
          height: '24px',
          borderRadius: '12px',
          border: 'none',
          backgroundColor: formData.active ? '#166534' : '#d1d5db',
          position: 'relative',
          cursor: 'pointer',
          transition: 'background-color 0.2s ease',
          flexShrink: 0,
          padding: 0
        }}
      >
        <span style={{
          position: 'absolute',
          top: '2px',
          left: formData.active ? '22px' : '2px',
          width: '20px',
          height: '20px',
          borderRadius: '50%',
          backgroundColor: 'white',
          transition: 'left 0.2s ease',
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
        }} />
      </button>
      <div style={{ textAlign: 'left' }}>
        <span className="toggle-title" style={{ fontSize: '0.95rem', fontWeight: '600', color: formData.active ? '#166534' : '#6b7280' }}>
          Listing is {formData.active ? 'active' : 'inactive'}
        </span>
        <span className="toggle-subtitle" style={{ display: 'block', fontSize: '0.8rem', color: formData.active ? '#166534' : '#9ca3af', textAlign: 'left' }}>
          {formData.active ? 'Enables trade matching and incoming offers' : 'Hidden from trade matching'}
        </span>
      </div>
      {editingPedal?.needsReactivation && !formData.active && (
        <span className="toggle-subtitle" style={{ 
          fontSize: '0.8rem',
          color: '#dc2626',
          textAlign: 'left'
        }}>
          ⚠️ <strong>To reactivate:</strong> Toggle on, update description, and upload new images
        </span>
      )}
      <style>{`
        @media (max-width: 768px) {
          .toggle-title {
            font-size: 0.85rem !important;
          }
          .toggle-subtitle {
            font-size: 0.7rem !important;
          }
          .active-toggle-container {
            padding: 0.6rem 1rem !important;
          }
          .offer-mode-compact {
            margin-bottom: 0.75rem !important;
          }
          .offer-mode-compact button {
            font-size: 0.9rem !important;
            padding: 0.7rem 0.4rem !important;
          }
        }
      `}</style>
    </div>
  );

  return (
    <div className="bg-white rounded-lg shadow-md p-6 max-w-2xl mx-auto w-full add-pedal-outer">

      <h2 className="text-2xl font-bold mb-4 text-center">{editingPedal ? 'Edit Pedal' : 'List a Pedal or Accessory'}</h2>

      {isWizard && (
        <div style={{ maxWidth: '850px', margin: '2rem auto 0.5rem auto' }}>
          <div style={{ display: 'flex', gap: '6px', marginBottom: '10px' }}>
            {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
              <div
                key={i}
                style={{
                  flex: 1,
                  height: '6px',
                  borderRadius: '9999px',
                  backgroundColor: i < currentStep ? '#4db6ac' : '#e5e7eb',
                  transition: 'background-color 0.3s ease',
                }}
              />
            ))}
          </div>
          <div style={{ fontSize: '0.9rem', color: '#6b7280', fontWeight: 600, textAlign: 'left' }}>
            Step {currentStep} of {TOTAL_STEPS}, {STEP_LABELS[currentStep - 1]}
          </div>
        </div>
      )}

      {successMsg && (
        <div ref={successRef} className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          <p className="font-bold" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', margin: 0 }}>
            {/* Brand checkmark (teal circle + white tick) — matches the want-list
                SuccessCard so listing/updating shows the same icon, not the old ✅. */}
            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '24px', height: '24px', borderRadius: '9999px', backgroundColor: '#4db6ac', flexShrink: 0 }}>
              <svg width="14" height="14" viewBox="0 0 12 12" fill="none"><path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </span>
            {successMsg.replace(/^✅\s*/, '')}
          </p>
          {(successMsg === '✅ Pedal listed successfully!' ||
            successMsg === '✅ Pedal reactivated as a new listing!') && (
            <p className="mt-2 text-sm font-normal text-green-800">
              Now visit the{' '}
              <Link
                to="/latest-pedals"
                state={{ fresh: true }}
                className="font-semibold hover:underline"
                style={{ color: '#C9A227' }}
              >
                Browse Pedals
              </Link>{' '}
              page to start trading!
            </p>
          )}
        </div>
      )}
      {errorMsg && (
        // Plain red bold line (Jason, 2026-10-18: no icon). Messages are still
        // built with a leading "❌" in a dozen places; strip it here once.
        <div ref={errorRef} style={{ margin: '0 0 1rem 0', textAlign: 'center' }}>
          <p style={{ color: '#dc2626', fontWeight: 700, fontFamily: 'Nunito, sans-serif', margin: 0 }}>{String(errorMsg).replace(/^\s*❌\s*/, '')}</p>
        </div>
      )}
      
      {editingPedal?.needsReactivation && (
        <div style={{ 
          backgroundColor: '#fff3cd', 
          border: '1px solid #ffeaa7', 
          borderRadius: '4px', 
          padding: '1rem', 
          marginBottom: '1rem',
          color: '#856404'
        }}>
          <h4 style={{ margin: '0 0 0.5rem 0', fontWeight: 'bold' }}>🔄 Reactivation Required</h4>
          <p style={{ margin: 0, fontSize: '0.9rem' }}>
            This pedal was deactivated after a completed trade. To reactivate it, you must:
            <br />• Upload at least one new image
            <br />• Update the description to reflect current condition
          </p>
          {formData.active && !editingPedal.active && (
            <div 
              style={{ 
                backgroundColor: '#fef2f2', 
                border: '1px solid #fecaca', 
                borderRadius: '4px', 
                padding: '0.5rem', 
                marginTop: '0.5rem',
                color: '#dc2626',
                fontSize: '0.8rem'
              }}>
              ⚠️ <strong>Warning:</strong> You've checked "Active" but haven't made the required changes yet. 
              To reactivate this pedal, you need BOTH new images AND an updated description.
            </div>
          )}
        </div>
      )}

      {sellingDisabled && (
        <div style={{ width: '100%', maxWidth: '850px', margin: '0 auto 1rem' }}>
          <SellingDisabledBanner mode="full" />
        </div>
      )}

      <form
        id={ADD_PEDAL_FORM_ID}
        onSubmit={handleSubmit}
        onKeyDown={handleKeyDown}
        noValidate
        className="space-y-4 add-pedal-root-form"
        style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}
      >
        <style>{`
          @media (min-width: 769px) {
            .add-pedal-root-form .add-pedal-form-container,
            .add-pedal-root-form .create-listing-images-root {
              width: 100%;
              max-width: 850px;
              margin-left: auto;
              margin-right: auto;
              box-sizing: border-box;
            }
            .add-pedal-root-form .wizard-attr-grid {
              grid-template-columns: 1fr 1fr 1fr !important;
            }
            .add-pedal-root-form .wizard-attr-condition {
              grid-column: auto !important;
            }
            .add-pedal-root-form .create-listing-angle-guides {
              display: flex;
              flex-direction: row;
              flex-wrap: nowrap;
              justify-content: center;
              align-items: center;
              gap: 0.75rem;
              width: 100%;
              box-sizing: border-box;
            }
            .add-pedal-root-form .create-listing-angle-guides img {
              flex: 1 1 0;
              min-width: 0;
              width: 100%;
              max-height: 120px;
              object-fit: contain;
            }
            .add-pedal-root-form .create-listing-angle-guides img:nth-child(3),
            .add-pedal-root-form .create-listing-angle-guides img:nth-child(4) {
              max-height: 152px;
              flex: 1.15 1 0;
            }
            .add-pedal-root-form [data-create-thumb-strip] {
              justify-content: flex-start;
            }
          }
          @media (max-width: 768px) {
            .add-pedal-root-form .wizard-attr-grid {
              grid-template-columns: 1fr 1fr !important;
            }
            .add-pedal-root-form .wizard-attr-condition {
              grid-column: 1 / -1 !important;
            }
            .add-pedal-root-form > .add-pedal-image-section {
              max-width: 28rem !important;
              margin: 1rem 0.5rem !important;
              box-sizing: border-box !important;
              min-width: 0;
            }
            .add-pedal-root-form > .create-listing-images-root {
              box-sizing: border-box !important;
              min-width: 0;
              width: calc(100% - 1rem) !important;
              max-width: 28rem !important;
              margin: 1rem 0.5rem !important;
              padding: 1.5rem !important;
            }
            .add-pedal-root-form .create-listing-angle-guides {
              display: grid;
              grid-template-columns: 1fr 1fr;
              column-gap: 0.75rem;
              row-gap: 1.75rem;
              width: 100%;
              box-sizing: border-box;
            }
            .add-pedal-root-form .create-listing-angle-guides img {
              width: 100%;
              max-height: 120px;
              object-fit: contain;
            }
            .add-pedal-root-form .create-listing-angle-guides img:nth-child(3),
            .add-pedal-root-form .create-listing-angle-guides img:nth-child(4) {
              max-height: 152px;
            }
            .add-pedal-root-form [data-create-thumb-strip] {
              justify-content: center;
            }
          }
        `}</style>
        {/* Editing: the on/off switch is what sellers come here for most
            often, so it sits first instead of below the whole form. */}
        {editingPedal && activeToggle}
        {editingPedal && offerModeTogglesCompact}
        {/* ── STEP 1: identify pedal ─────────────────────────────────────── */}
        <div className="add-pedal-form-container" style={{ ...WIZARD_CARD_STYLE, ...(stepStyle(1) || {}) }}>
          {(isWizard || isAdminEdit) && (
            <div style={{ textAlign: 'left' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '1.05rem', fontWeight: 700, color: '#1f2937' }}>
                Search for your pedal
              </label>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', pointerEvents: 'none' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                </span>
                <input
                  type="text"
                  value={pedalQuery}
                  onChange={(e) => {
                    setPedalQuery(e.target.value);
                    setPedalSelected(false);
                    setShowCombinedSuggestions(true);
                    setPedalVerified(false);
                    setReverbCatalog(null);
                    setFormData((p) => ({ ...p, make: '', model: '' }));
                  }}
                  onFocus={() => { if (!pedalSelected) setShowCombinedSuggestions(true); }}
                  placeholder="e.g. ThorpyFX Field Marshal"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                  className="border border-gray-300 focus:border-blue-500 focus:outline-none"
                  style={{ width: '100%', borderRadius: '25px', fontSize: '17px', padding: '0.55rem 0.9rem 0.55rem 2.6rem', borderWidth: (isInvalid('make') || isInvalid('model')) ? '2px' : '1px', borderColor: (isInvalid('make') || isInvalid('model')) ? '#dc2626' : undefined, boxSizing: 'border-box' }}
                />
              </div>

              {!pedalSelected && showCombinedSuggestions && pedalQuery.trim().length >= 2 && (combinedSuggestions.length > 0 || combinedLoading) && (
                <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {combinedSuggestions.length === 0 && combinedLoading ? (
                    <div style={{ color: '#6B7280', fontSize: '0.9rem', padding: '0.35rem 0' }}>Searching…</div>
                  ) : (
                    /* Display cap: the hook holds up to 24 candidates for
                       narrowing/typo recovery, but 8 rows is the readable
                       dropdown limit — narrowing surfaces the rest. */
                    combinedSuggestions.slice(0, 8).map((s, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => selectCombinedSuggestion(s)}
                        className="hover:bg-gray-50"
                        style={{ display: 'block', textAlign: 'left', background: 'white', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '0.7rem 0.95rem', cursor: 'pointer', width: '100%' }}
                      >
                        {s.make && (
                          <span style={{ display: 'block', color: '#1f2937', fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.03em', textTransform: 'uppercase' }}>{s.make}</span>
                        )}
                        <span style={{ display: 'block', fontWeight: 700, color: '#6b7280', fontSize: '1rem', lineHeight: 1.25 }}>{s.model || s.title}</span>
                      </button>
                    ))
                  )}
                </div>
              )}

              {pedalSelected && (formData.make || formData.model) && (
                <div style={{ marginTop: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.9rem', background: '#e7f6ec', border: '1px solid #b7e4c7', borderRadius: '14px', padding: '0.9rem 1.1rem' }}>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    {formData.make && <span style={{ display: 'block', color: '#1f2937', fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.03em', textTransform: 'uppercase' }}>{formData.make}</span>}
                    <span style={{ display: 'block', fontWeight: 700, color: '#4b5563', fontSize: '1.05rem', lineHeight: 1.25 }}>{formData.model || pedalQuery}</span>
                  </span>
                  <span style={{ color: '#2f9e57', flexShrink: 0 }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                  </span>
                </div>
              )}

              {combinedNoMatch && (
                <div style={{ marginTop: '0.9rem' }}>
                  <div style={{ textAlign: 'center', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '14px', padding: '1.1rem 1rem' }}>
                    <div style={{ color: '#9ca3af', marginBottom: '0.4rem', display: 'flex', justifyContent: 'center' }}>
                      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                    </div>
                    <div style={{ fontWeight: 700, color: '#374151' }}>No matches for &ldquo;{pedalQuery.trim()}&rdquo;</div>
                    <div style={{ color: '#6b7280', fontSize: '0.9rem', marginTop: '0.15rem' }}>Check the spelling, or enter the brand &amp; model manually below.</div>
                  </div>
                  <div style={{ marginTop: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.9rem', fontWeight: 600 }}>
                      <span style={{ color: 'red' }}>*</span>Brand:
                    </label>
                    <input
                      name="make"
                      value={formData.make}
                      onChange={handleChange}
                      onBlur={() => capitalizeManualField('make')}
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck="false"
                      placeholder="e.g. Bonecrusher Fuzzworks"
                      className="border border-gray-300 focus:border-blue-500 focus:outline-none px-3"
                      style={{ width: '100%', borderRadius: '25px', fontSize: '17px', paddingTop: '0.33rem', paddingBottom: '0.33rem', borderWidth: isInvalid('make') ? '2px' : '1px', borderColor: isInvalid('make') ? '#dc2626' : undefined, boxSizing: 'border-box' }}
                    />
                  </div>
                  <div style={{ marginTop: '0.8rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.9rem', fontWeight: 600 }}>
                      <span style={{ color: 'red' }}>*</span>Model:
                    </label>
                    <input
                      name="model"
                      value={formData.model}
                      onChange={handleChange}
                      onBlur={() => capitalizeManualField('model')}
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck="false"
                      placeholder="e.g. V2"
                      className="border border-gray-300 focus:border-blue-500 focus:outline-none px-3"
                      style={{ width: '100%', borderRadius: '25px', fontSize: '17px', paddingTop: '0.33rem', paddingBottom: '0.33rem', borderWidth: isInvalid('model') ? '2px' : '1px', borderColor: isInvalid('model') ? '#dc2626' : undefined, boxSizing: 'border-box' }}
                    />
                  </div>

                  {/* "Found it after all" — the manual text matched a real
                      catalog pedal (typo fixed / word finished). Tappable so
                      the user lands on the standardized name; ignorable so
                      manual entry is never blocked. */}
                  {manualMatches.length > 0 && (
                    <div style={{ marginTop: '1rem' }}>
                      <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#2f9e57', marginBottom: '0.4rem' }}>
                        Good news — this pedal is in our catalog. Tap to use the standard name:
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {manualMatches.slice(0, 3).map((s, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => selectCombinedSuggestion(s)}
                            className="hover:bg-gray-50"
                            style={{ display: 'block', textAlign: 'left', background: 'white', border: '1px solid #b7e4c7', borderRadius: '12px', padding: '0.7rem 0.95rem', cursor: 'pointer', width: '100%' }}
                          >
                            {s.make && (
                              <span style={{ display: 'block', color: '#1f2937', fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.03em', textTransform: 'uppercase' }}>{s.make}</span>
                            )}
                            <span style={{ display: 'block', fontWeight: 700, color: '#6b7280', fontSize: '1rem', lineHeight: 1.25 }}>{s.model || s.title}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {(pedalSelected || combinedNoMatch) && (
                <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                  <button type="button" onClick={resetPedalSelection} style={{ background: 'none', border: 'none', color: '#5A9ECB', fontWeight: 700, cursor: 'pointer', fontSize: '0.95rem' }}>
                    Not this? Search again
                  </button>
                </div>
              )}
            </div>
          )}
          <style>{`
            @media (min-width: 769px) {
              .addpedal-description-textarea {
                width: 100% !important;
                box-sizing: border-box !important;
                font-size: 16px !important;
                height: 120px !important;
                overflow-y: auto !important;
                resize: vertical !important;
              }
            }
            @media (max-width: 768px) {
              .add-pedal-outer {
                max-width: 100% !important;
                padding-left: 0.15rem !important;
                padding-right: 0.15rem !important;
              }
              .add-pedal-form-container {
                max-width: none !important;
                margin: 1rem 0 !important;
                padding: 1.5rem !important;
              }
              
              .add-pedal-form-container input,
              .add-pedal-form-container select {
                margin-right: 0 !important;
                box-sizing: border-box !important;
                width: 100% !important;
              }
              .addpedal-description-textarea {
                width: 100% !important;
                margin-left: 0 !important;
                margin-right: 0 !important;
                box-sizing: border-box !important;
                min-height: 100px !important;
                overflow-y: auto !important;
                resize: vertical !important;
                font-size: 16px !important;
              }
            }
          `}</style>
          {!isWizard && !isAdminEdit && (
          <div
            className="add-pedal-form-grid"
            style={{ marginBottom: '1rem', marginTop: '1.5rem', display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: '1.25rem', rowGap: '1rem', position: 'relative' }}
          >
            <style>{`
              @media (max-width: 768px) {
                .add-pedal-form-grid {
                  grid-template-columns: 1fr !important;
                  column-gap: 0 !important;
                  row-gap: 0.15rem !important;
                  padding: 0 !important;
                  margin-top: 0.75rem !important;
                }
                .add-pedal-form-grid label {
                  font-weight: 600 !important;
                  font-size: 0.9rem !important;
                  margin-top: 0.5rem !important;
                }
                .add-pedal-form-grid input,
                .add-pedal-form-grid select {
                  width: 100% !important;
                  margin-right: 0 !important;
                  box-sizing: border-box !important;
                }
              }
            `}</style>
            
            <div style={{ position: 'relative' }}>
              <label style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.9rem', fontWeight: '600', textAlign: 'left' }}>
                <span style={{ color: 'red' }}>*</span>Brand:
              </label>
              <input 
                name="make" 
                value={formData.make} 
                onChange={handleChange} 
                onFocus={() => {
                  if (formData.make && (displayBrandSuggestions.length > 0 || brandLoading)) {
                    setShowBrandSuggestions(true);
                  }
                }}
                onBlur={(e) => {
                  handleBlur(e);
                  capitalizeManualField('make');
                  setTimeout(() => setShowBrandSuggestions(false), 200);
                }}
                required 
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
                data-form-type="other"
                className="border border-gray-300 focus:border-blue-500 focus:outline-none px-3"
                style={{ borderRadius: '25px', fontSize: '17px', verticalAlign: 'middle', borderWidth: '1px', fontWeight: 'normal', width: '100%', textTransform: 'none', paddingTop: '0.33rem', paddingBottom: '0.33rem' }}
              />
              {showBrandSuggestions && (displayBrandSuggestions.length > 0 || brandLoading) && (
                <div className="absolute z-20 w-full bg-white border-2 border-blue-200 rounded-lg mt-1 max-h-48 overflow-y-auto shadow-xl">
                  <div className="px-3 py-2 bg-blue-50 border-b-2 border-blue-100 text-xs font-bold text-blue-800 rounded-t-lg">
                    <span className="font-bold">Suggestions:</span>
                  </div>
                  {displayBrandSuggestions.length === 0 && brandLoading ? (
                    <div className="px-3 py-2 text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: '#6B7280' }}>
                      Searching brands…
                    </div>
                  ) : (
                    displayBrandSuggestions.map((brand, index) => (
                      <button
                        key={index}
                        className="hover:opacity-80 transition-opacity"
                        style={{
                          backgroundColor: '#E5E7EB',
                          borderRadius: '9999px',
                          border: 'none',
                          padding: '8px 16px',
                          fontFamily: 'Nunito, sans-serif',
                          color: '#374151',
                          fontWeight: '600',
                          fontSize: '14px',
                          cursor: 'pointer',
                          margin: '2px 8px',
                          width: 'calc(100% - 16px)',
                          minHeight: '28px',
                          display: 'block'
                        }}
                        onClick={() => selectBrandSuggestion(brand)}
                        type="button"
                      >
                        {brand}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
            
            <div style={{ position: 'relative' }}>
              <label style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.9rem', fontWeight: '600', textAlign: 'left' }}>
                <span style={{ color: 'red' }}>*</span>Model:
              </label>
              <input 
                name="model" 
                value={formData.model} 
                onChange={handleChange} 
                onFocus={() => {
                  if (formData.model && (displayModelSuggestions.length > 0 || modelLoading)) {
                    setShowModelSuggestions(true);
                  }
                }}
                onBlur={(e) => {
                  handleBlur(e);
                  capitalizeManualField('model');
                  setTimeout(() => setShowModelSuggestions(false), 200);
                }}
                required 
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
                data-form-type="other"
                className="border border-gray-300 focus:border-blue-500 focus:outline-none px-3"
                style={{ borderRadius: '25px', fontSize: '17px', verticalAlign: 'middle', borderWidth: '1px', fontWeight: 'normal', width: '100%', textTransform: 'none', paddingTop: '0.33rem', paddingBottom: '0.33rem' }}
              />
              {showModelSuggestions && (displayModelSuggestions.length > 0 || modelLoading) && (
                <div className="absolute z-20 w-full bg-white border-2 border-green-200 rounded-lg mt-1 max-h-48 overflow-y-auto shadow-xl">
                  <div className="px-3 py-2 bg-green-50 border-b-2 border-green-100 text-xs font-bold text-green-800 rounded-t-lg">
                    <span className="font-bold">Suggestions:</span>
                  </div>
                  {displayModelSuggestions.length === 0 && modelLoading ? (
                    <div className="px-3 py-2 text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: '#6B7280' }}>
                      Searching models…
                    </div>
                  ) : (
                    displayModelSuggestions.map((suggestion, index) => (
                      <button
                        key={index}
                        className="hover:opacity-80 transition-opacity"
                        style={{
                          backgroundColor: '#E5E7EB',
                          borderRadius: '9999px',
                          border: 'none',
                          padding: '8px 16px',
                          fontFamily: 'Nunito, sans-serif',
                          color: '#374151',
                          fontWeight: '600',
                          fontSize: '14px',
                          cursor: 'pointer',
                          margin: '2px 8px',
                          width: 'calc(100% - 16px)',
                          minHeight: '28px',
                          display: 'block'
                        }}
                        onClick={() => selectModelSuggestion(suggestion)}
                        type="button"
                      >
                        {suggestion.label}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Missing Pedal Status and Manual Add Button */}
            {!suggestionsVisible && isNewEntry && (
              <div style={{ gridColumn: '1 / -1', marginTop: '10px' }}>
                <div style={{ 
                  backgroundColor: '#f0f9ff', 
                  border: '1px solid #5A9ECB', 
                  borderRadius: '8px', 
                  padding: '12px',
                  fontSize: '14px',
                  color: '#0c4a6e'
                }}>
                  Hmm, we don&apos;t recognize this pedal. Double-check your spelling before listing!
                </div>
              </div>
            )}
          </div>
          )}
        </div>

        {/* ── STEP 3: price + price check only ────────────────────────────── */}
        <div className="add-pedal-form-container" style={{ ...WIZARD_CARD_STYLE, ...(stepStyle(3) || {}) }}>
          <div
            className="add-pedal-form-grid"
            style={{ marginTop: '0.5rem', marginBottom: '0.5rem', display: 'grid', gridTemplateColumns: '1fr', rowGap: '1rem', position: 'relative' }}
          >
            {/*
              Gold "Fair price" banner ABOVE the price input (per design): the
              user sees the sold-informed fair price + Good-to-High range for
              their pedal (Step 1) and condition (Step 2) BEFORE typing a
              price. The style is applied directly to PriceIndicator's outer
              div so that when it returns null (no data / loading in compact)
              the grid cell vanishes too — no empty band. While the range is
              loading the full variant shows a same-height skeleton, so the
              card doesn't jump when the banner lands.
            */}
            <PriceIndicator
              make={formData.make}
              model={formData.model}
              condition={formData.condition}
              price={Number(formData.price) || 0}
              decade={formData.decade}
              priceInputRef={priceInputRef}
              style={{ gridColumn: '1 / -1' }}
              /* Only the wizard's dedicated price step needs the placeholder to
                 hold the banner's space. In edit mode the form is one continuous
                 scroll, so a grey block that appears above the price field and
                 then vanishes when the lookup comes back empty just looks like
                 something failed to load. */
              showSkeleton={isWizard}
            />

            <div>
              <label style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.9rem', fontWeight: '600', textAlign: 'left' }}>
                <span style={{ color: 'red' }}>*</span>Price:
              </label>
              <input 
                ref={priceInputRef}
                name="price" 
                type="text" 
                inputMode="decimal"
                value={formData.price} 
                onChange={(e) => setFormData({...formData, price: formatCurrencyInput(e.target.value)})}
                required 
                className="border border-gray-300 focus:border-blue-500 focus:outline-none px-3"
                style={{ borderRadius: '25px', fontSize: '17px', verticalAlign: 'middle', borderWidth: isInvalid('price') ? '2px' : '1px', borderColor: isInvalid('price') ? '#dc2626' : undefined, fontWeight: 'normal', width: '100%', paddingTop: '0.33rem', paddingBottom: '0.33rem' }}
              />
            </div>
          </div>
        </div>

        {/* ── STEP 2 (attributes): condition + decade + color ─────────────── */}
        <div className="add-pedal-form-container" style={{ ...WIZARD_CARD_STYLE, ...(stepStyle(2) || {}) }}>
          <div
            className="add-pedal-form-grid wizard-attr-grid"
            style={{ marginTop: '0.5rem', marginBottom: '0.5rem', display: 'grid', columnGap: '1.25rem', rowGap: '1rem', position: 'relative' }}
          >
            <div className="wizard-attr-condition">
              <label style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.9rem', fontWeight: '600', textAlign: 'left' }}>
                <span style={{ color: 'red' }}>*</span>Condition:
              </label>
              <select 
                ref={conditionSelectRef}
                name="condition" 
                value={formData.condition} 
                onChange={(e) => { handleChange(e); setConditionJustPicked(Boolean(e.target.value)); setConditionPopoverDismissed(false); }} 
                required
                className="pxc-form-select"
                style={{ ...FORM_SELECT_STYLE, ...(isInvalid('condition') ? FORM_SELECT_INVALID_STYLE : {}) }}
              >
                <option value="">Select</option>
                <option value="New">New</option>
                <option value="Excellent">Excellent</option>
                <option value="Very Good">Very Good</option>
                <option value="Good">Good</option>
                <option value="Poor">Poor</option>
                <option value="Damaged">Damaged</option>
              </select>
            </div>

            <ConditionDescriptionPopover
              anchorRef={conditionSelectRef}
              visible={conditionJustPicked && Boolean(formData.condition) && !conditionPopoverDismissed}
              description={CONDITION_DESCRIPTIONS[formData.condition]}
              onDismiss={() => setConditionPopoverDismissed(true)}
            />

            <div>
              <label style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.9rem', fontWeight: '600', textAlign: 'left' }}>
                Decade:
              </label>
              <select 
                name="decade" 
                value={formData.decade} 
                onChange={handleChange}
                className="pxc-form-select"
                style={FORM_SELECT_STYLE}
              >
                <option value="">Any</option>
                <option value="1970s">1970s</option>
                <option value="1980s">1980s</option>
                <option value="1990s">1990s</option>
                <option value="2000s">2000s</option>
                <option value="2010s">2010s</option>
                <option value="2020s">2020s</option>
              </select>
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.9rem', fontWeight: '600', textAlign: 'left' }}>
                Color:
              </label>
              <select 
                name="color" 
                value={formData.color} 
                onChange={handleChange}
                className="pxc-form-select"
                style={FORM_SELECT_STYLE}
              >
                <option value="">Any</option>
                {PEDAL_COLORS.map(color => (
                  <option key={color} value={color}>{color}</option>
                ))}
              </select>
            </div>
            
          </div>
        </div>

        {/* ── STEP 2: describe (effects, accessories, description) ────────── */}
        <div style={stepStyle(2)}>

        <div 
          className="add-pedal-form-container"
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
            padding: '2rem',
            maxWidth: '850px',
            margin: '1rem auto'
          }}
        >
          {/* Effect Types */}
          <div style={{ marginBottom: '1rem', marginTop: '-0.5rem' }}>
            <style>{`
              .effect-type-btn:hover {
                box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
                transform: translateY(-1px);
              }
              @media (max-width: 768px) {
                .effect-types-grid {
                  gap: 0.3rem !important;
                }
                .effect-type-btn {
                  padding: 6px 14px !important;
                  font-size: 0.8rem !important;
                }
                .effect-type-label {
                  margin-top: 0.75rem !important;
                }
              }
            `}</style>
            <p className="text-sm" style={{ marginBottom: '0.5rem', color: '#6b7280' }}><span style={{ color: 'red' }}>*</span> Please select at least one effect type or accessory type</p>
            <label className="font-bold effect-type-label" style={{ display: 'block', marginBottom: '0.25rem', marginTop: '0.75rem', color: isInvalid('effectTypes') ? '#dc2626' : undefined }}>
              Effect Type(s):
            </label>
            {isInvalid('effectTypes') && (
              <div style={{ fontSize: '0.8rem', color: '#dc2626', marginBottom: '0.25rem' }}>
                Select at least one effect or accessory type.
              </div>
            )}
            <div style={{ height: '0.75rem' }} />
            <div 
              className="effect-types-grid"
              style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', justifyContent: 'center', marginBottom: '1.25rem' }}
            >
                {EFFECT_TYPES.map((type) => {
                  const isSelected = formData.effectTypes.includes(type);
                  return (
                    <button
                      key={type}
                      type="button"
                      className="effect-type-btn"
                      onClick={() => {
                        setFormData((prev) => {
                          const updated = isSelected
                            ? prev.effectTypes.filter((t) => t !== type)
                            : [...prev.effectTypes, type];
                          return { ...prev, effectTypes: updated };
                        });
                      }}
                      style={{
                        padding: '8px 18px',
                        borderRadius: '9999px',
                        border: isSelected ? '1.5px solid #C9A227' : '1.5px solid #d1d5db',
                        backgroundColor: isSelected ? 'rgba(201, 162, 39, 0.12)' : 'white',
                        color: isSelected ? '#946F00' : '#374151',
                        fontWeight: isSelected ? '700' : '500',
                        fontSize: '0.9rem',
                        fontFamily: 'Nunito, sans-serif',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      {type}
                    </button>
                  );
                })}
            </div>

            {/* Accessories */}
            <label className="font-bold" style={{ display: 'block', marginBottom: '0.25rem', marginTop: '1.5rem' }}>
              Accessories:
            </label>
            <div style={{ height: '0.75rem' }} />
            <div 
              className="effect-types-grid"
              style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', justifyContent: 'center' }}
            >
                {[
                  'Power Supply', 'Pedalboard', 'Other Accessory'
                ].map((type) => {
                  const isSelected = formData.effectTypes.includes(type);
                  return (
                    <button
                      key={type}
                      type="button"
                      className="effect-type-btn"
                      onClick={() => {
                        setFormData((prev) => {
                          const updated = isSelected
                            ? prev.effectTypes.filter((t) => t !== type)
                            : [...prev.effectTypes, type];
                          return { ...prev, effectTypes: updated };
                        });
                      }}
                      style={{
                        padding: '8px 18px',
                        borderRadius: '9999px',
                        border: isSelected ? '1.5px solid #C9A227' : '1.5px solid #d1d5db',
                        backgroundColor: isSelected ? 'rgba(201, 162, 39, 0.12)' : 'white',
                        color: isSelected ? '#946F00' : '#374151',
                        fontWeight: isSelected ? '700' : '500',
                        fontSize: '0.9rem',
                        fontFamily: 'Nunito, sans-serif',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      {type}
                    </button>
                  );
                })}
            </div>
          </div>
        </div>

        <div 
          className="add-pedal-form-container"
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
            padding: '2rem',
            maxWidth: '850px',
            margin: '1rem auto'
          }}
        >
          {/* Description */}
          <div>
            <label className="font-bold block" style={{ marginBottom: '0.25rem' }}>Description: <span style={{ fontWeight: 400, color: '#6B7280', fontSize: '0.85rem' }}>(optional)</span></label>
            <div className="w-full" style={{ marginTop: '0.5rem', marginBottom: '1rem' }}>
              <textarea
                name="description"
                placeholder="Describe the condition, operation, accessories, and if it's vintage."
                value={formData.description}
                onChange={handleChange}
                rows={5}
                maxLength={DESCRIPTION_LIMIT}
                className="border border-gray-300 focus:border-blue-500 focus:outline-none px-3 py-2 w-full addpedal-description-textarea"
                style={{ borderRadius: '6px', borderWidth: isInvalid('description') ? '2px' : undefined, borderColor: isInvalid('description') ? '#dc2626' : undefined }}
              />
              <div
                className={`text-xs mt-1 ${
                  formData.description.length >= DESCRIPTION_LIMIT ? 'text-red-500' : 'text-gray-500'
                }`}
              >
                {DESCRIPTION_LIMIT - formData.description.length} characters remaining
              </div>
            </div>
          </div>
          {!editingPedal && offerModeToggles}
        </div>
        </div>

        {/* ── STEP 4: finish (photos, video, shipping, status, list) ──────── */}
        <div style={stepStyle(4)}>


        {useBulkPhotoPipeline ? (
        <div
          key={formResetKey}
          className="create-listing-images-root"
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
            padding: '2rem',
            marginTop: '2rem',
            marginBottom: '1rem',
            boxSizing: 'border-box'
          }}
        >
          <div className="add-pedal-image-section" style={{ marginBottom: 0, marginTop: 0 }}>
            <h3 style={{ fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '0.5rem', textAlign: 'center' }}>{skipPhotos ? 'Photos' : <><span style={{ color: 'red' }}>*</span> Min. 2 Images Required</>}</h3>
            <div style={{ fontSize: '1.025rem', color: '#666', marginBottom: '1rem', textAlign: 'center', lineHeight: '1.5' }}>
              <div style={{ fontWeight: 'bold' }}>{skipPhotos ? 'You can add photos any time from your Pedalboard.' : 'Follow the example angles to show users this is a genuine, original listing.'}</div>
            </div>
            {!editingPedal && PHOTOS_LATER_ENABLED && (
              <label
                className="add-photos-later-choice"
                onClick={(e) => e.stopPropagation()}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer',
                  background: skipPhotos ? '#FBF3DF' : '#FAFAFA', border: `1px solid ${skipPhotos ? '#C89F3C' : '#E5E7EB'}`,
                  borderRadius: '12px', padding: '0.75rem 1rem', marginBottom: '1rem',
                  fontFamily: 'Nunito, sans-serif', textAlign: 'left'
                }}
              >
                <input
                  type="checkbox"
                  checked={skipPhotos}
                  onChange={(e) => {
                    setSkipPhotos(e.target.checked);
                    if (e.target.checked && GIVEAWAY_WARNING_ENABLED) setShowGiveawayPhotoWarning(true);
                  }}
                  style={{ marginTop: '3px', accentColor: '#C89F3C' }}
                />
                <span>
                  <span style={{ display: 'block', fontWeight: 700, color: '#374151' }}>Add photos later</span>
                  <span style={{ display: 'block', fontSize: '0.85rem', color: '#6B7280', lineHeight: 1.45 }}>
                    Original photos will be required to accept any offer.{GIVEAWAY_WARNING_ENABLED ? ' Does not count toward the giveaway until photos are added.' : ''}
                  </span>
                </span>
              </label>
            )}
            {showGiveawayPhotoWarning && (
              <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="giveaway-photo-warning-title"
                onClick={(e) => e.stopPropagation()}
                style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
              >
                <div style={{ background: 'white', borderRadius: '16px', padding: '24px 22px', maxWidth: '460px', width: '100%', fontFamily: 'Nunito, sans-serif', textAlign: 'left', boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}>
                  <div id="giveaway-photo-warning-title" style={{ fontWeight: 800, fontSize: '1.35rem', color: '#374151', marginBottom: '10px' }}>Heads up about the giveaway</div>
                  <div style={{ fontSize: '1.1rem', color: '#4B5563', lineHeight: 1.55, marginBottom: '20px' }}>
                    A listing without photos does not count toward the giveaway until you add real photos of this pedal. You can still trade and sell it as normal. Photos will be required before any offer can be accepted.
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowGiveawayPhotoWarning(false)}
                    style={{ width: '100%', border: 'none', background: '#C89F3C', color: 'white', borderRadius: '25px', padding: '14px 18px', fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '1.1rem', cursor: 'pointer' }}
                  >
                    Got it
                  </button>
                </div>
              </div>
            )}
            <div
              role="group"
              tabIndex={0}
              aria-label="Listing photos. Drop images here, tap empty space to browse, or press Enter or Space when this area is focused."
              onKeyDown={(e) => {
                if (e.target !== e.currentTarget) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openPhotoPicker();
                }
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                addFiles(e.dataTransfer.files);
              }}
              onClick={(e) => {
                if (suppressBrowseAfterDragRef.current) return;
                if (e.target.closest('[data-create-thumb-strip]')) return;
                openPhotoPicker();
              }}
              style={{
                width: '100%',
                minHeight: orderedFiles.length ? undefined : 200,
                padding: '1rem',
                border: '2px dashed #ccc',
                borderRadius: 10,
                backgroundColor: 'white',
                cursor: 'pointer',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                outline: 'none',
                gap: '0.75rem'
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => {
                  if (e.target.files?.length) addFiles(e.target.files);
                }}
              />
              {orderedFiles.length === 0 && skipPhotos && !editingPedal ? (
                // The stand-in, shown like an uploaded photo so it is obvious what
                // the listing will look like (Jason, 2026-10-18).
                <div style={{ display: 'flex', justifyContent: 'center', padding: '0.5rem 0' }}>
                  <div style={{ position: 'relative', width: '160px', height: '160px', borderRadius: '10px', overflow: 'hidden', border: '1px solid #E5E7EB', background: '#F5F1E8' }}>
                    <img
                      src={buildPlaceholderImage(reverbCatalog).url}
                      alt="Default stand-in image"
                      draggable={false}
                      onDragStart={(e) => e.preventDefault()}
                      onError={(e) => { if (!e.currentTarget.src.endsWith(SKETCH_PLACEHOLDER_URL)) e.currentTarget.src = SKETCH_PLACEHOLDER_URL; }}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                    <div style={{ position: 'absolute', bottom: '6px', left: '6px', background: 'rgba(255,255,255,0.95)', color: '#4A5759', fontFamily: 'Nunito, sans-serif', fontSize: '11px', fontWeight: 700, letterSpacing: '0.3px', padding: '3px 8px', borderRadius: '9999px', boxShadow: '0 1px 4px rgba(0,0,0,0.2)' }}>
                      Default
                    </div>
                  </div>
                </div>
              ) : orderedFiles.length === 0 ? (
                <div className="create-listing-angle-guides">
                  <img src="/images/top-diagram.png" alt="Top angle guide" draggable={false} onDragStart={(e) => e.preventDefault()} />
                  <img src="/images/back-diagram.png" alt="Back angle guide" draggable={false} onDragStart={(e) => e.preventDefault()} />
                  <img src="/images/upper-diagram.png" alt="Upper angle guide" draggable={false} onDragStart={(e) => e.preventDefault()} />
                  <img src="/images/lower-diagram.png" alt="Lower angle guide" draggable={false} onDragStart={(e) => e.preventDefault()} />
                </div>
              ) : null}
              {orderedFiles.length > 0 ? (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleCreatePhotosDragEnd}
                  onDragCancel={handleCreatePhotosDragCancel}
                >
                  <SortableContext items={createListingSortableIds} strategy={createListingPhotoSortStrategy}>
                    <div
                      data-create-thumb-strip
                      onClick={(e) => e.stopPropagation()}
                      onDragStart={(e) => e.preventDefault()}
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '0.5rem',
                        width: '100%',
                        cursor: 'default',
                        userSelect: 'none',
                        WebkitUserSelect: 'none'
                      }}
                    >
                      {orderedFiles.map((file, index) => (
                        <SortableCreatePhotoThumb
                          key={getSortableIdForFile(file)}
                          id={getSortableIdForFile(file)}
                          url={previewUrls[index]}
                          index={index}
                          onRemove={() => removePhotoAt(index)}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              ) : null}
              {orderedFiles.length > 0 && orderedFiles.length < 8 && (
                <button
                  type="button"
                  onClick={(e) => {
                    // Without this the tap ALSO bubbles to the box's onClick —
                    // two picker activations in one gesture (see openPhotoPicker).
                    e.stopPropagation();
                    openPhotoPicker();
                  }}
                  style={{
                    marginTop: '0.5rem',
                    fontSize: 'calc(13px + 1pt)',
                    color: '#5A9ECB',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px 0'
                  }}
                >
                  + Add more photos ({orderedFiles.length}/8)
                </button>
              )}
            </div>
            <p style={{ textAlign: 'center', marginTop: '0.75rem', marginBottom: 0, fontSize: '0.9rem', color: '#555', lineHeight: 1.5, width: '100%', boxSizing: 'border-box' }}>
              Drop up to 8 photos here, or tap to select. Drag thumbnails to reorder. First photo is your hero shot.
            </p>
          </div>
        </div>
        ) : (
        <>
        <div style={{ marginTop: '2rem', marginBottom: '0.5rem', textAlign: 'center' }}>
          <button
            type="button"
            onClick={handleReplaceAllPhotos}
            disabled={isLoading || sellingDisabled}
            title={sellingDisabled ? 'Selling, trading & listing are paused — pay outstanding invoice to restore' : undefined}
            style={{
              backgroundColor: sellingDisabled ? '#9CA3AF' : 'white',
              color: sellingDisabled ? '#fff' : '#374151',
              border: `1px solid ${sellingDisabled ? '#9CA3AF' : '#C89F3C'}`,
              borderRadius: '9999px',
              padding: '10px 22px',
              fontFamily: 'Nunito, sans-serif',
              fontWeight: 700,
              fontSize: '15px',
              cursor: (isLoading || sellingDisabled) ? 'not-allowed' : 'pointer',
              opacity: (isLoading || sellingDisabled) ? 0.7 : 1
            }}
          >
            Replace All Photos
          </button>
        </div>
        <div className="add-pedal-image-section" style={{ marginBottom: '2rem', marginTop: '0.5rem' }}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '0.5rem', textAlign: 'center' }}><span style={{ color: 'red' }}>*</span> Min. 2 Images Required</h3>
          <div style={{ fontSize: '1.025rem', color: '#666', marginBottom: '1rem', textAlign: 'center', lineHeight: '1.5' }}>
            <div style={{ fontWeight: 'bold' }}>Follow the example angles to show users this is a genuine, original listing.</div>
          </div>
          <div className="image-tiles-container" style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', justifyContent: 'center' }}>
            <style>{`
              .image-tiles-container > div {
                width: 280px;
              }
              .image-preview-box {
                aspect-ratio: 1 / 1;
              }
              @media (max-width: 768px) {
                .image-tiles-container {
                  gap: 0.5rem !important;
                  padding: 0 !important;
                  width: 100% !important;
                }
                .image-tiles-container > div {
                  width: calc(50% - 0.25rem) !important;
                  max-width: none !important;
                  flex: 1 1 calc(50% - 0.25rem) !important;
                }
              }
            `}</style>
            {[
              { label: 'Top View', key: 'top', diagram: '/images/top-diagram.png' },
              { label: 'Back View', key: 'back', diagram: '/images/back-diagram.png' },
              { label: 'Upper 3/4 View', key: 'upper', diagram: '/images/upper-diagram.png' },
              { label: 'Lower 3/4 View', key: 'lower', diagram: '/images/lower-diagram.png' }
            ].map(({ label, key, diagram }) => (
              <EditModeImageTile
                key={`${key}-${formResetKey}`}
                slot={key}
                label={label}
                required={['top', 'back'].includes(key)}
                variant="required"
                diagramSrc={diagram}
                file={images[key]}
                existingUrl={editingPedal?.images?.[key]}
                isRemoved={removedExistingImageKeys.has(key)}
                onPick={handlePickImage}
                onRemove={handleRemoveImage}
              />
            ))}
          </div>
        </div>

        <div className="add-pedal-image-section" style={{ marginBottom: '2rem' }}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '1rem', textAlign: 'center', color: '#666' }}>Additional Images (Optional but encouraged)</h3>
          <p style={{ textAlign: 'center', marginBottom: '1rem', fontSize: '0.9rem', color: '#666' }}>
            Add more photos to help buyers see details, damage, or unique features
          </p>
          <div className="image-tiles-container" style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', justifyContent: 'center' }}>
            {[
              { label: 'Detail Shot 1', key: 'detail1', placeholder: 'Close-up of knobs, switches, or damage' },
              { label: 'Detail Shot 2', key: 'detail2', placeholder: 'Serial number, inside, or other details' },
              { label: 'Detail Shot 3', key: 'detail3', placeholder: 'Side view, bottom, or unique features' },
              { label: 'Detail Shot 4', key: 'detail4', placeholder: 'Any other important angle or detail' }
            ].map(({ label, key, placeholder }) => (
              <EditModeImageTile
                key={`${key}-${formResetKey}`}
                slot={key}
                label={label}
                required={false}
                variant="optional"
                placeholderText={placeholder}
                file={images[key]}
                existingUrl={editingPedal?.images?.[key]}
                isRemoved={removedExistingImageKeys.has(key)}
                onPick={handlePickImage}
                onRemove={handleRemoveImage}
              />
            ))}
          </div>
        </div>
        </>
        )}


        {/* YouTube Demo Video (Optional) */}
        <div
          className="add-pedal-form-container"
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
            padding: '2rem',
            maxWidth: '850px',
            margin: '1rem auto 2rem auto',
            boxSizing: 'border-box'
          }}
        >
          <h3 style={{ fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '0.5rem', textAlign: 'center', color: '#666' }}>YouTube Demo Video (Optional)</h3>
          <p style={{ textAlign: 'center', marginBottom: '0.75rem', fontSize: '0.9rem', color: '#666' }}>
            Add a YouTube link to a demo of your pedal
          </p>
          <div style={{ width: '100%', boxSizing: 'border-box' }}>
            <input
              type="url"
              placeholder="https://www.youtube.com/watch?v=..."
              value={formData.youtubeUrl}
              onChange={(e) => setFormData({ ...formData, youtubeUrl: e.target.value })}
              className="border border-gray-300 focus:border-blue-500 focus:outline-none px-3"
              style={{
                width: '100%',
                borderRadius: '25px',
                fontSize: '15px',
                padding: '10px 16px',
                borderWidth: '1px',
                boxSizing: 'border-box'
              }}
            />
            {formData.youtubeUrl && (() => {
              const ytMatch = formData.youtubeUrl.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([a-zA-Z0-9_-]{11})/);
              if (ytMatch) {
                return (
                  <div style={{ marginTop: '0.75rem', textAlign: 'center' }}>
                    <img
                      src={`https://img.youtube.com/vi/${ytMatch[1]}/mqdefault.jpg`}
                      alt="Video thumbnail"
                      style={{ maxWidth: '100%', width: '200px', height: 'auto', borderRadius: '8px', border: '1px solid #ddd' }}
                    />
                    <p style={{ fontSize: '0.8rem', color: '#166534', marginTop: '0.25rem' }}>Video linked successfully</p>
                  </div>
                );
              }
              return <p style={{ fontSize: '0.8rem', color: '#dc2626', marginTop: '0.5rem', textAlign: 'center' }}>Please enter a valid YouTube URL</p>;
            })()}
          </div>
        </div>

        {/* Shipping line — auto-filled from profile */}
        <div className="add-pedal-form-container" style={{ ...WIZARD_CARD_STYLE }}>
          <label style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.9rem', fontWeight: '600', textAlign: 'left' }}>
            Ships from:
          </label>
          <input
            name="location"
            value={formData.location}
            onChange={handleChange}
            required
            disabled
            className="border border-gray-300 bg-gray-100 text-gray-700 px-3 cursor-not-allowed"
            style={{ borderRadius: '25px', fontSize: '17px', width: '100%', paddingTop: '0.33rem', paddingBottom: '0.33rem', boxSizing: 'border-box' }}
            title="Location is automatically set from your profile shipping address"
          />
        </div>

        {!editingPedal && activeToggle}

        {isLoading && !editingPedal && (
          <p className="text-gray-600 mt-4 italic">
            Uploading pedal... ⏳
          </p>
        )}

        </div>

        {isWizard && (
          <div style={{ display: 'flex', gap: '0.75rem', maxWidth: '850px', margin: '0.5rem auto 0', alignItems: 'stretch' }}>
            {/* Suppress the focus ring on pointer clicks (advancing a step
                leaves the Continue button focused, which showed a lingering
                blue outline) while keeping it for keyboard users (a11y). */}
            <style>{`
              .wizard-nav-btn:focus:not(:focus-visible) {
                outline: none;
                box-shadow: none;
              }
            `}</style>
            {currentStep > 1 && (
              <button
                type="button"
                onClick={goBack}
                className="hover:bg-gray-50 wizard-nav-btn"
                style={{ borderRadius: '9999px', border: '1px solid #d1d5db', background: 'white', color: '#374151', fontWeight: 700, fontSize: '16px', padding: '12px 28px', cursor: 'pointer', fontFamily: 'Nunito, sans-serif' }}
              >
                Back
              </button>
            )}
            {currentStep < TOTAL_STEPS && (
              <button
                type="button"
                onClick={goNext}
                className="hover:opacity-80 transition-opacity wizard-nav-btn"
                style={{ flex: 1, borderRadius: '9999px', border: 'none', backgroundColor: '#C9A227', color: 'white', fontWeight: 'bold', fontSize: '16px', padding: '12px 24px', fontFamily: 'Nunito, sans-serif', cursor: 'pointer' }}
              >
                Continue
              </button>
            )}
            {currentStep === TOTAL_STEPS && (
              <button
                type="submit"
                disabled={isLoading || sellingDisabled}
                title={sellingDisabled ? 'Selling, trading & listing are paused — pay outstanding invoice to restore' : undefined}
                className="hover:opacity-80 transition-opacity wizard-nav-btn"
                style={{ flex: 1, borderRadius: '9999px', border: 'none', backgroundColor: sellingDisabled ? '#9CA3AF' : '#C9A227', color: 'white', fontWeight: 'bold', fontSize: '16px', padding: '12px 24px', fontFamily: 'Nunito, sans-serif', cursor: sellingDisabled ? 'not-allowed' : 'pointer', opacity: sellingDisabled ? 0.7 : 1 }}
              >
                {sellingDisabled ? 'Selling paused' : 'List Pedal'}
              </button>
            )}
          </div>
        )}
      </form>

      {editingPedal && (
        <div style={{ textAlign: 'center', marginTop: '1rem' }}>
          <button
            type="button"
            onClick={onFinishEdit}
            style={{ color: '#666', textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px' }}
          >
            ✖ Cancel Edit
          </button>
        </div>
      )}

      {/* Report Bug Section */}
      <div className='text-center pt-4 border-t border-gray-200' style={{ marginTop: '4rem' }}>
        <p className='text-gray-600 text-sm'>
          Need help or found a bug?{' '}
          <button 
            onClick={() => openContact({ category: 'support' })}
            style={{ 
              color: '#5A9ECB',
              background: 'none',
              border: 'none',
              padding: 0,
              font: 'inherit',
              cursor: 'pointer'
            }}
            className='hover:underline'
          >
            Contact Support
          </button>
        </p>
      </div>
      {editingPedal && (
        <>
          <style>{`
            .floating-save-changes-btn {
              position: fixed;
              bottom: 42px;
              left: 30px;
              z-index: 1000;
              border-radius: 9999px;
              border: none;
              padding: 14px 30px;
              font-family: Nunito, sans-serif;
              color: white;
              font-weight: 700;
              font-size: 17px;
              background-color: #C9A227;
              cursor: pointer;
              box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
              transition: background-color 0.3s ease, transform 0.3s ease, opacity 0.3s ease;
            }
            .floating-save-changes-btn.is-paused {
              background-color: #9CA3AF;
            }
            .floating-save-changes-btn:disabled {
              cursor: not-allowed;
              opacity: 0.7;
            }
            /* Hover belongs in CSS behind a hover query rather than in JS mouse
               handlers. iOS synthesizes mouseenter on whatever a tap lands near
               and only clears it on the next tap elsewhere, so JS handlers made
               this permanently-visible fixed button pulse on every single tap. */
            @media (hover: hover) and (pointer: fine) {
              .floating-save-changes-btn:not(:disabled):hover {
                background-color: #B89020;
                transform: scale(1.04);
              }
            }
            @media (max-width: 768px) {
              .floating-save-changes-btn {
                bottom: calc(92px + env(safe-area-inset-bottom));
                left: 14px;
                padding: 12px 24px;
                font-size: 16px;
              }
            }
          `}</style>
          {/* Calls handleSubmit directly instead of relying on type="submit" plus
              a form="" association. This button sits outside the <form>, and in
              edit mode it is the ONLY submit control the form has — the in-form
              one is inside the isWizard branch — so there is no fallback path if
              that association fails. */}
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isLoading || sellingDisabled}
            title={sellingDisabled ? 'Selling, trading & listing are paused — pay outstanding invoice to restore' : undefined}
            className={`floating-save-changes-btn${sellingDisabled ? ' is-paused' : ''}`}
            aria-label="Save changes to this pedal"
          >
            {isLoading ? 'Saving…' : sellingDisabled ? 'Selling paused' : 'Save Changes'}
          </button>
        </>
      )}
    </div>
  );
};

export default AddPedalForm;
